/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Alert, Box, Collapse, Menu, Typography} from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import {ColumnHeaderFilterIconButtonProps, DataGridPro, GridCallbackDetails, GridColDef, GridColumnHeaderParams, GridColumnMenuContainer, GridColumnMenuHideItem, GridColumnMenuPinningItem, GridColumnMenuProps, GridColumnMenuSortItem, GridColumnOrderChangeParams, GridColumnResizeParams, GridColumnVisibilityModel, GridDensity, GridEventListener, GridPinnedColumnFields, gridPreferencePanelStateSelector, GridPreferencePanelsValue, GridRowId, GridRowModel, GridRowParams, GridRowsProp, GridRowSelectionModel, GridSortItem, GridSortModel, GridToolbarContainer, GridToolbarDensitySelector, MuiEvent, useGridApiContext, useGridApiEventHandler, useGridApiRef} from "@mui/x-data-grid-pro";
import {QController} from "@temple-of-syrinx/qqq-frontend-core/lib/controllers/QController";
import {ApiVersion} from "@temple-of-syrinx/qqq-frontend-core/lib/controllers/QControllerV1";
import {Capability} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/Capability";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QFieldType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldType";
import {QInstance} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QInstance";
import {QProcessMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QProcessMetaData";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QTableVariant} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableVariant";
import {QJobComplete} from "@temple-of-syrinx/qqq-frontend-core/lib/model/processes/QJobComplete";
import {QJobError} from "@temple-of-syrinx/qqq-frontend-core/lib/model/processes/QJobError";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import {QCriteriaOperator} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QCriteriaOperator";
import {QFilterCriteria} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QFilterCriteria";
import {QFilterOrderBy} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QFilterOrderBy";
import {QQueryFilter} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QQueryFilter";
import QContext from "QContext";
import colors from "qqq/assets/theme/base/colors";
import {QActionsMenuButton, QCancelButton, QCreateNewButton} from "qqq/components/buttons/DefaultButtons";
import MenuButton from "qqq/components/buttons/MenuButton";
import ErrorBoundary from "qqq/components/misc/ErrorBoundary";
import {GotoRecordButton} from "qqq/components/misc/GotoRecordDialog";
import SavedViews from "qqq/components/misc/SavedViews";
import BasicAndAdvancedQueryControls from "qqq/components/query/BasicAndAdvancedQueryControls";
import {CustomFilterPanel} from "qqq/components/query/CustomFilterPanel";
import CustomPaginationComponent from "qqq/components/query/CustomPaginationComponent";
import ExportMenuItem from "qqq/components/query/ExportMenuItem";
import FieldListMenu from "qqq/components/query/FieldListMenu";
import {validateCriteria} from "qqq/components/query/FilterCriteriaRow";
import {ActionMenuActions, ActionMenuItem, ItemsShownInMenu} from "qqq/components/menus/ActionMenuItems";
import {QMenu} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QMenu";
import {RecordViewMenu} from "qqq/components/view/RecordViewMenus";
import SelectionSubsetDialog from "qqq/components/query/SelectionSubsetDialog";
import TableVariantDialog from "qqq/components/query/TableVariantDialog";
import useSavedViews from "qqq/components/query/useSavedViews";
import CustomWidthTooltip from "qqq/components/tooltips/CustomWidthTooltip";
import BaseLayout from "qqq/layouts/BaseLayout";
import {LoadingState} from "qqq/models/LoadingState";
import QQueryColumns, {PreLoadQueryColumns} from "qqq/models/query/QQueryColumns";
import RecordQueryView from "qqq/models/query/RecordQueryView";
import ProcessRun from "qqq/pages/processes/ProcessRun";
import ColumnStats from "qqq/pages/records/query/ColumnStats";
import DataGridUtils from "qqq/utils/DataGridUtils";
import {AnalyticsModel} from "qqq/utils/analytics/AnalyticsUtils";
import Client from "qqq/utils/qqq/Client";
import FilterUtils from "qqq/utils/qqq/FilterUtils";
import ProcessUtils from "qqq/utils/qqq/ProcessUtils";
import {SavedViewUtils} from "qqq/utils/qqq/SavedViewUtils";
import TableUtils from "qqq/utils/qqq/TableUtils";
import ValueUtils from "qqq/utils/qqq/ValueUtils";
import React, {forwardRef, useCallback, useContext, useEffect, useId, useImperativeHandle, useMemo, useReducer, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useRowEditor} from "qqq/components/grid/rowEdit/useRowEditor";
import {makeEditableRow} from "qqq/components/grid/rowEdit/EditableRow";
import {EditingRowBannerOverlay} from "qqq/components/grid/rowEdit/EditingRowBannerOverlay";
import {HoverRowActionsOverlay} from "qqq/components/grid/rowEdit/HoverRowActionsOverlay";
import {SavedRowOverlay} from "qqq/components/grid/rowEdit/SavedRowOverlay";
import {RowEditContext, RowEditContextValue} from "qqq/components/grid/rowEdit/RowEditContext";
import ExpandedGridRowsContext, {ExpandedGridRows} from "qqq/components/query/ExpandedGridRowsContext";
import {RowEditCellRenderer} from "qqq/components/grid/rowEdit/cells/RowEditCellRenderer";
import {bumpColumnWidthsForEdit, captureGridScroll, preserveFirstVisibleColumn} from "qqq/components/grid/rowEdit/rowEditUtils";

const CURRENT_SAVED_VIEW_ID_LOCAL_STORAGE_KEY_ROOT = "qqq.currentSavedViewId";
const DENSITY_LOCAL_STORAGE_KEY_ROOT = "qqq.density";
const VIEW_LOCAL_STORAGE_KEY_ROOT = "qqq.recordQueryView";

export const TABLE_VARIANT_LOCAL_STORAGE_KEY_ROOT = "qqq.tableVariant";
export type QueryScreenUsage = "queryScreen" | "reportSetup" | "recordPicker"

interface Props
{
   table?: QTableMetaData,
   apiVersion?: ApiVersion,
   launchProcess?: QProcessMetaData,
   usage?: QueryScreenUsage,
   isModal?: boolean,
   isPreview?: boolean,
   initialQueryFilter?: QQueryFilter,
   initialColumns?: QQueryColumns,
   allowVariables?: boolean,
   omitExposedJoins?: string[],

   ///////////////////////////////////////////////////////////////////////////
   // When set, locks the tableVariant for this query:                      //
   //   - Used as the variant instead of any localStorage value (and the    //
   //     localStorage entry for this table is not written).                //
   //   - The TableVariantDialog prompt is suppressed.                      //
   //   - The "change variant" gear in the page header is hidden.           //
   //   - In modal mode, a read-only variant indicator is rendered so users //
   //     can still see which variant scopes the query.                     //
   // Hosts (e.g. FilterAndColumnsSetupWidget when its widgetData declares  //
   // variantTypeKey + variantSourceFieldName) pass this in so the variant  //
   // tracks the parent record's value.                                     //
   ///////////////////////////////////////////////////////////////////////////
   forcedTableVariant?: QTableVariant,

   ////////////////////////////////////////////////////////////////////////////
   // for usage="recordPicker" - "single" restricts the grid to one row at a //
   // time, drawn with radios rather than checkboxes.                        //
   ////////////////////////////////////////////////////////////////////////////
   selectionMode?: "single" | "multiple",

   ///////////////////////////////////////////////////////////////////////////
   // how many of the LEADING criteria in the initialQueryFilter are locked //
   // - shown read-only, and not removable by the user.                     //
   ///////////////////////////////////////////////////////////////////////////
   lockedCriteriaCount?: number,
}

///////////////////////////////////////////////////////
// define possible values for our pageState variable //
///////////////////////////////////////////////////////
type PageState = "initial" | "loadingMetaData" | "loadedMetaData" | "loadingView" | "loadedView" | "preparingGrid" | "ready" | "error";

const qController = Client.getInstance();
const qControllerV1 = Client.getInstanceV1();


/*******************************************************************************
 ** css for the single-select record-picker's selection column, making the control
 ** read as a radio: a round outline, and a plain filled circle when selected.
 **
 ** Done purely in css - deliberately NOT by swapping the grid's BaseCheckbox slot
 ** for a component rendering radio icons.  Two reasons:
 **
 ** 1. this app's MuiCheckbox theme (assets/theme/components/form/checkbox.ts)
 **    sets the icon svg's color to transparent and draws the visible control with
 **    a css border plus a checkmark background-image, so swapping the icon element
 **    changes nothing a user can see.
 ** 2. a components={{...}} slot has to be a stable component identity.  Defined
 **    inline it is a new function every render, and since a cellMouseDown listener
 **    here sets state, a render lands between mousedown and mouseup - React then
 **    remounts the checkbox, destroying the input mid-click so the click never
 **    completes and selection silently fails.  No slot override, no such hazard.
 **
 ** !important on the two properties the theme also sets at equal specificity
 ** (.MuiCheckbox-colorPrimary.Mui-checked .MuiSvgIcon-root), so this does not
 ** depend on stylesheet ordering.
 *******************************************************************************/
const singleSelectPickerRadioSx = (accentColor: string) => ({
   "& .MuiDataGrid-cellCheckbox .MuiSvgIcon-root": {
      borderRadius: "50% !important",
   },
   "& .MuiDataGrid-cellCheckbox .Mui-checked .MuiSvgIcon-root": {
      backgroundImage: "none !important",
      backgroundColor: accentColor,
      borderColor: accentColor,
   },

   ////////////////////////////////////////////////////////////////////////////////
   // single-select has no "select all" - hide the header's control.  it is done //
   // in css because DataGrid injects that column itself, so there is no colDef  //
   // of ours to override.                                                       //
   ////////////////////////////////////////////////////////////////////////////////
   "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {display: "none"},
});

/*******************************************************************************
 ** function to produce standard version of the screen while we're "loading"
 ** like the main table meta data etc.
 *******************************************************************************/
const getLoadingScreen = (isModal: boolean) =>
{
   return (<Box>&nbsp;</Box>);
};


/***************************************************************************
 * The exported version of RecordQuery that pages & other components use here,
 * is actually a wrapper around the true component (RecordQueryInner).
 * The wrapper adds an ErrorBoundary, with an alert with a link that *tries* to fix
 * issues that we've seen (e.g., when a bad filter gets put into local storage).
 ***************************************************************************/
const RecordQuery = forwardRef((props: Props, ref) =>
{

   /***************************************************************************
    * try to fix issues by removing the current view from local storage
    * and reloading the page.  It fixes some issues at least!
    ***************************************************************************/
   const fixIt = () =>
   {
      //////////////////////////////////////////////////////////////////
      // remove the current view and saved-view-id from local storage //
      //////////////////////////////////////////////////////////////////
      localStorage.removeItem(`${VIEW_LOCAL_STORAGE_KEY_ROOT}.${props.table?.name}`);
      localStorage.removeItem(`${CURRENT_SAVED_VIEW_ID_LOCAL_STORAGE_KEY_ROOT}.${props.table?.name}`);

      ////////////////////////////////////////////////////////////////////////////////////
      // if the URL looks like we're using a saved view, try to navigate away from that //
      ////////////////////////////////////////////////////////////////////////////////////
      if (location.href.indexOf("/savedView/") > -1)
      {
         location.href = location.href.replace(/\/savedView\/.*/, "");
      }
      else
      {
         ///////////////////////////////
         // else just reload the page //
         ///////////////////////////////
         location.reload();
      }
   };

   const errorElement = <Box>
      <h3>Error</h3>
      <Alert severity="error">
         An error occurred loading this page. You may try to <a href="#" onClick={fixIt}>click here to fix it</a>.
      </Alert>
   </Box>;

   const body = <ErrorBoundary errorElement={errorElement}>
      <RecordQueryInner {...props} ref={ref} />
   </ErrorBoundary>;

   if (props.isModal)
   {
      return (body);
   }

   return (<BaseLayout>{body}</BaseLayout>);
});


/*******************************************************************************
 ** QQQ Record Query Screen component.
 **
 ** Yuge component.  The best.  Lots of very smart people are saying so.
 *******************************************************************************/
const RecordQueryInner = forwardRef(({table, apiVersion, usage, isModal, isPreview, allowVariables, initialQueryFilter, initialColumns, omitExposedJoins, forcedTableVariant, selectionMode, lockedCriteriaCount}: Props, ref) =>
{
   const tableName = table.name;
   const [searchParams] = useSearchParams();

   const [showSuccessfullyDeletedAlert, setShowSuccessfullyDeletedAlert] = useState(false);
   const [errorAlert, setErrorAlert] = useState(null as string);
   const [warningAlert, setWarningAlert] = useState(null as string);
   const [warningAlertList, setWarningAlertList] = useState([] as string[]);
   const [successAlert, setSuccessAlert] = useState(null as string);
   const [infoAlert, setInfoAlert] = useState(null as string);

   const navigate = useNavigate();
   const location = useLocation();
   const pathParts = location.pathname.replace(/\/+$/, "").split("/");

   const [firstRender, setFirstRender] = useState(true);
   const [isFirstRenderAfterChangingTables, setIsFirstRenderAfterChangingTables] = useState(false);

   const [loadedFilterFromInitialFilterParam, setLoadedFilterFromInitialFilterParam] = useState(false);

   const mayWriteLocalStorage = usage == "queryScreen";


   /*******************************************************************************
    **
    *******************************************************************************/
   function localStorageSet(key: string, value: string)
   {
      if (mayWriteLocalStorage)
      {
         localStorage.setItem(key, value);
      }
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function localStorageRemove(key: string)
   {
      if (mayWriteLocalStorage)
      {
         localStorage.removeItem(key);
      }
   }

   useImperativeHandle(ref, () =>
   {
      return {
         getCurrentView(): RecordQueryView
         {
            return view;
         },

         ///////////////////////////////////////////////////////////////////////////////////
         // for usage="recordPicker" - let the host component read the user's selection   //
         // (in the same shape that gets handed to processes - ids, or a prepped filter). //
         ///////////////////////////////////////////////////////////////////////////////////
         getSelection(): string[] | QQueryFilter
         {
            return getSelectionAsRecordIdsOrFilter();
         },

         ////////////////////////////////////////////////////////////////////////////////
         // as getSelection, but resolved all the way to record ids (filter-based     //
         // selections are queried here, join/variant-aware), capped at the limit.    //
         ////////////////////////////////////////////////////////////////////////////////
         getSelectionAsRecordIds(limit: number): Promise<string[]>
         {
            return getSelectionAsRecordIds(limit);
         }
      };
   });

   ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
   // manage "state" being passed from some screens (like delete) into query screen - by grabbing, and then deleting //
   //                                                                                                                //
   // queryScreen usage only: `location` is the HOST PAGE's.  A picker or report-setup modal opened on a page that   //
   // was navigated to with {deleteSuccess} or {warning} would otherwise render the host's alert inside the modal,   //
   // and delete the key from history state so the host never gets to show it.                                       //
   ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
   if (usage == "queryScreen" && location.state)
   {
      let state: any = location.state;
      if (state["deleteSuccess"])
      {
         setShowSuccessfullyDeletedAlert(true);
         delete state["deleteSuccess"];
      }

      if (state["warning"])
      {
         setWarningAlert(state["warning"]);
         delete state["warning"];
      }

      window.history.replaceState(state, "");
   }

   ///////////////////////////////////////////////////////////////////////////////////////////////
   // look for defaults in the local storage                                                    //
   // note a few of these keys are duplicated in the wrapper where we try to catch & fix errors //
   ///////////////////////////////////////////////////////////////////////////////////////////////
   const currentSavedViewLocalStorageKey = `${CURRENT_SAVED_VIEW_ID_LOCAL_STORAGE_KEY_ROOT}.${tableName}`;
   const tableVariantLocalStorageKey = `${TABLE_VARIANT_LOCAL_STORAGE_KEY_ROOT}.${tableName}`;
   const viewLocalStorageKey = `${VIEW_LOCAL_STORAGE_KEY_ROOT}.${tableName}`;

   /////////////////////////////////////////////////////////////////////////////////////////////////
   // define some default values (e.g., to be used if nothing in local storage or no active view) //
   /////////////////////////////////////////////////////////////////////////////////////////////////
   let defaultSort = [] as GridSortItem[];
   let defaultRowsPerPage = 50;
   let defaultDensity = "standard" as GridDensity;
   let defaultTableVariant: QTableVariant = null;
   let defaultMode = "basic";
   let defaultQueryColumns: QQueryColumns = new PreLoadQueryColumns();
   let defaultView: RecordQueryView = null;

   /////////////////////////////////////
   // set density not to be per-table //
   /////////////////////////////////////
   const densityLocalStorageKey = `${DENSITY_LOCAL_STORAGE_KEY_ROOT}`;

   ///////////////////////////////////////////////////////////////
   // only load things out of local storage on the first render //
   ///////////////////////////////////////////////////////////////
   if (firstRender)
   {
      console.log("This is firstRender, so reading defaults from local storage...");
      if (localStorage.getItem(densityLocalStorageKey))
      {
         defaultDensity = JSON.parse(localStorage.getItem(densityLocalStorageKey));
      }
      //////////////////////////////////////////////////////////////////////
      // forcedTableVariant overrides any localStorage value; never write //
      // localStorage when the host is forcing a variant.                 //
      //////////////////////////////////////////////////////////////////////
      if (forcedTableVariant)
      {
         defaultTableVariant = forcedTableVariant;
      }
      else if (localStorage.getItem(tableVariantLocalStorageKey))
      {
         defaultTableVariant = JSON.parse(localStorage.getItem(tableVariantLocalStorageKey));
      }
      if (localStorage.getItem(viewLocalStorageKey))
      {
         defaultView = RecordQueryView.buildFromJSON(localStorage.getItem(viewLocalStorageKey));
      }

      setFirstRender(false);
   }

   if (defaultView == null)
   {
      defaultView = new RecordQueryView();
      defaultView.queryFilter = new QQueryFilter();
      defaultView.queryColumns = defaultQueryColumns;
      defaultView.viewIdentity = "empty";
      defaultView.rowsPerPage = defaultRowsPerPage;
      /////////////////////////////////////////////////
      // ... defaultView.quickFilterFieldNames = []; //
      /////////////////////////////////////////////////
      defaultView.mode = defaultMode;
   }

   if (firstRender)
   {
      /////////////////////////////////////////////////////////////////////////
      // allow a caller to send in an initial filter & set of columns.       //
      // only to be used on "first render".                                  //
      // JSON.parse(JSON.stringify()) to do deep clone and keep object clean //
      // unclear why not needed on initialColumns...                         //
      /////////////////////////////////////////////////////////////////////////
      if (initialQueryFilter)
      {
         defaultView.queryFilter = JSON.parse(JSON.stringify(initialQueryFilter));
         setLoadedFilterFromInitialFilterParam(true);
      }

      if (initialColumns)
      {
         defaultView.queryColumns = initialColumns;
      }
   }

   /////////////////////////////////////////////////////////////////////////////////////////
   // in case the view is missing any of these attributes, give them a reasonable default //
   /////////////////////////////////////////////////////////////////////////////////////////
   if (!defaultView.rowsPerPage)
   {
      defaultView.rowsPerPage = defaultRowsPerPage;
   }
   if (!defaultView.mode)
   {
      defaultView.mode = defaultMode;
   }
   if (!defaultView.quickFilterFieldNames)
   {
      defaultView.quickFilterFieldNames = [];
   }

   ///////////////////////////////////
   // state models for the DataGrid //
   ///////////////////////////////////
   const [columnSortModel, setColumnSortModel] = useState(defaultSort);
   const [columnVisibilityModel, setColumnVisibilityModel] = useState(defaultQueryColumns.toColumnVisibilityModel());
   const [columnsModel, setColumnsModel] = useState([] as GridColDef[]);
   const [density, setDensity] = useState(defaultDensity);
   const [loading, setLoading] = useState(true);
   const [pageNumber, setPageNumber] = useState(0);
   const [pinnedColumns, setPinnedColumns] = useState(defaultQueryColumns.toGridPinnedColumns());
   const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
   const [rows, setRows] = useState([] as GridRowsProp[]);
   const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(new Set());
   const rowsRef = useRef<GridRowsProp[]>([]);
   useEffect(() =>
   {
      rowsRef.current = rows as GridRowsProp[];
   }, [rows]);
   const [rowsPerPage, setRowsPerPage] = useState(defaultView.rowsPerPage);
   const [totalRecords, setTotalRecords] = useState(null);
   const gridApiRef = useGridApiRef();
   const [pulsingField, setPulsingField] = useState<string | null>(null);

   //////////////////////
   // row-edit state   //
   //////////////////////
   const [editingRowId, setEditingRowId] = useState<string | number | null>(null);
   const [editingRow, setEditingRow] = useState<QRecord | null>(null);
   const formikSubmitRef = useRef<(() => void) | null>(null);

   /////////////////////////////////////////////////////////////////////////////////////
   // Set while we drive columnVisibilityModel changes ourselves (forcing required    //
   // editable fields visible at edit-entry, and restoring at edit-exit).  Causes the //
   // visibility-change handler to skip its "cancel the active edit" branch — that    //
   // branch is meant to fire only on USER-driven changes, but MUI X's setColumnWidth //
   // can also trip it (see bumpColumnWidthsForEdit in rowEditUtils.ts).              //
   /////////////////////////////////////////////////////////////////////////////////////
   const isAdjustingColumnVisibilityForEditRef = useRef(false);

   /////////////////////////////////////////////////////////////////////////////////////
   // Snapshot of columnVisibilityModel taken just before we augment it at edit-entry //
   // (to make required-but-hidden fields visible).  Restored at edit-exit so the     //
   // user's chosen column visibility returns to what it was pre-edit.                //
   /////////////////////////////////////////////////////////////////////////////////////
   const preEditColumnVisibilityModelRef = useRef<GridColumnVisibilityModel | null>(null);

   ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
   // state of the page - e.g., have we loaded meta data?  what about the initial view?  or are we ready to render records. //
   ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
   const [pageState, setPageState] = useState("initial" as PageState);

   /////////////////////////////////
   // meta-data and derived state //
   /////////////////////////////////
   const [metaData, setMetaData] = useState(null as QInstance);
   const [tableMetaData, setTableMetaData] = useState(null as QTableMetaData);
   const [tableLabel, setTableLabel] = useState("");
   const [tableProcesses, setTableProcesses] = useState([] as QProcessMetaData[]);
   const [allTableProcesses, setAllTableProcesses] = useState([] as QProcessMetaData[]);

   ///////////////////////////////////////////
   // state of the view of the query screen //
   ///////////////////////////////////////////
   const [view, setView] = useState(defaultView);
   const [viewAsJson, setViewAsJson] = useState(JSON.stringify(defaultView));
   const [queryFilter, setQueryFilter] = useState(defaultView.queryFilter);
   const [queryColumns, setQueryColumns] = useState(defaultView.queryColumns);
   const [lastFetchedQFilterJSON, setLastFetchedQFilterJSON] = useState("");
   const [lastFetchedVariant, setLastFetchedVariant] = useState(null);
   const [tableVariant, setTableVariant] = useState(defaultTableVariant);
   const [quickFilterFieldNames, setQuickFilterFieldNames] = useState(defaultView.quickFilterFieldNames);

   //////////////////////////////////////////////////////////////////////////
   // when the host changes forcedTableVariant (e.g. the parent record's   //
   // connectionId changed in the form), retrack — push the new value into //
   // state so dependent effects re-fetch under the right variant.         //
   //////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (!forcedTableVariant)
      {
         return;
      }
      const currentJson = JSON.stringify(tableVariant ?? null);
      const forcedJson  = JSON.stringify(forcedTableVariant);
      if (currentJson !== forcedJson)
      {
         setTableVariant(forcedTableVariant);
      }
   }, [JSON.stringify(forcedTableVariant ?? null)]);

   ////////////////////////////////////////////////////////////////////////////////
   // when tableVariant changes due to a forcedTableVariant update on an already //
   // loaded page, re-fetch so rows reflect the new variant immediately.         //
   // Guarded by forcedTableVariant so the user-prompt path (which goes through  //
   // tableVariantPromptOpen → the pageState effect) does not double-fetch.      //
   ////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (pageState === "ready" && forcedTableVariant && !tableVariantPromptOpen)
      {
         updateTable("forcedTableVariant changed");
      }
   }, [JSON.stringify(tableVariant ?? null)]);

   //////////////////////////////////////////////
   // misc state... needs grouped & documented //
   //////////////////////////////////////////////
   const [visibleJoinTables, setVisibleJoinTables] = useState(new Set<string>());
   const [virtualFieldNamesToInclude, setVirtualFieldNamesToInclude] = useState([] as string[]);
   const [distinctRecords, setDistinctRecords] = useState(null);
   const [tableVariantPromptOpen, setTableVariantPromptOpen] = useState(false);
   const [alertContent, setAlertContent] = useState("");
   const [currentSavedView, setCurrentSavedView] = useState(null as QRecord);
   const [viewIdInLocation, setViewIdInLocation] = useState(null as number);
   const [loadingSavedView, setLoadingSavedView] = useState(false);
   const [exportMenuAnchorElement, setExportMenuAnchorElement] = useState(null);
   const [actionMenu, setActionMenu] = useState(null as QMenu);
   const [additionalActionMenus, setAdditionalActionMenus] = useState([] as QMenu[]);
   const [actionsMenuAnchorElement, setActionsMenuAnchorElement] = useState(null);
   const [tableDefaultView, setTableDefaultView] = useState(new RecordQueryView());

   /////////////////////////////////////////////////////
   // state related to avoiding accidental row clicks //
   /////////////////////////////////////////////////////
   const [gridMouseDownX, setGridMouseDownX] = useState(0);
   const [gridMouseDownY, setGridMouseDownY] = useState(0);
   const [gridPreferencesWindow, setGridPreferencesWindow] = useState(undefined);

   /////////////////////////////////////////////////////////////
   // state related to selecting records for using in actions //
   /////////////////////////////////////////////////////////////
   const [selectedIds, setSelectedIds] = useState([] as string[]);
   const [distinctRecordsOnPageCount, setDistinctRecordsOnPageCount] = useState(null as number);
   const [selectionSubsetSize, setSelectionSubsetSize] = useState(null as number);
   const [selectionSubsetSizePromptOpen, setSelectionSubsetSizePromptOpen] = useState(false);
   const [selectFullFilterState, setSelectFullFilterState] = useState("n/a" as "n/a" | "checked" | "filter" | "filterSubset");

   //////////////////////////////
   // state used for processes //
   //////////////////////////////
   const [activeModalProcess, setActiveModalProcess] = useState(null as QProcessMetaData);
   const [recordIdsForProcess, setRecordIdsForProcess] = useState([] as string[] | QQueryFilter);

   /////////////////////////////////////////
   // state used for column-stats feature //
   /////////////////////////////////////////
   const [columnStatsFieldName, setColumnStatsFieldName] = useState(null as string);
   const [columnStatsField, setColumnStatsField] = useState(null as QFieldMetaData);
   const [columnStatsFieldTableName, setColumnStatsFieldTableName] = useState(null as string);
   const [filterForColumnStats, setFilterForColumnStats] = useState(null as QQueryFilter);

   ///////////////////////////////////////////////////
   // state used for basic/advanced query component //
   ///////////////////////////////////////////////////
   const [mode, setMode] = useState(defaultView.mode);
   const basicAndAdvancedQueryControlsRef = useRef();

   /////////////////////////////////////////////////////////
   // a timer used to help avoid accidental double-clicks //
   /////////////////////////////////////////////////////////
   const timerInstance = useRef({timer: null});

   //////////////////////////////////////////////////////////////////////////////////////////////////////
   // state used to avoid showing results from an "old" query, that finishes loading after a newer one //
   //////////////////////////////////////////////////////////////////////////////////////////////////////
   const [latestQueryId, setLatestQueryId] = useState(0);
   const [countResults, setCountResults] = useState({} as any);
   const [receivedCountTimestamp, setReceivedCountTimestamp] = useState(new Date());
   const [queryResults, setQueryResults] = useState({} as any);
   const [latestQueryResults, setLatestQueryResults] = useState(null as QRecord[]);
   const [receivedQueryTimestamp, setReceivedQueryTimestamp] = useState(new Date());
   const [queryErrors, setQueryErrors] = useState({} as any);
   const [receivedQueryErrorTimestamp, setReceivedQueryErrorTimestamp] = useState(new Date());

   /////////////////////////////
   // page context references //
   /////////////////////////////
   const {accentColor, accentColorLight, setPageHeader, recordAnalytics, dotMenuOpen, keyboardHelpOpen, modalStack} = useContext(QContext);

   const useSavedViewsResult = useSavedViews({qController, metaData, tableMetaData});

   /***************************************************************************
    ** build a default query action menu if no metadata-driven one is defined
    ***************************************************************************/
   function buildDefaultQueryActionMenu(): QMenu
   {
      return new QMenu(
         {
            label: "Actions",
            icon: {name: "games"},
            slot: "QUERY_SCREEN_ACTIONS",
            items: [
               {
                  itemType: "SUB_LIST", values: {
                     items: [
                        {itemType: "BUILT_IN", values: {option: "BULK_LOAD"}},
                        {itemType: "BUILT_IN", values: {option: "BULK_EDIT"}},
                        {itemType: "BUILT_IN", values: {option: "BULK_EDIT_WITH_FILE"}},
                        {itemType: "BUILT_IN", values: {option: "BULK_DELETE"}}
                     ]
                  }
               },
               {itemType: "DIVIDER"},
               {itemType: "BUILT_IN", values: {option: "THIS_TABLE_PROCESS_LIST"}},
               {itemType: "DIVIDER"},
               {
                  itemType: "SUB_LIST", values: {
                     items: [
                        {itemType: "BUILT_IN", values: {option: "ALL_TABLES_PROCESS_LIST"}},
                        {itemType: "BUILT_IN", values: {option: "DEVELOPER_MODE"}}
                     ]
                  }
               }
            ]
         }
      );
   }

   /***************************************************************************
    ** get generic processes that should appear on all query/view screens
    ***************************************************************************/
   function getGenericProcesses(metaData: QInstance)
   {
      const genericProcesses: QProcessMetaData[] = [];
      const materialDashboardInstanceMetaData = metaData?.supplementalInstanceMetaData?.get("materialDashboard");
      if (materialDashboardInstanceMetaData)
      {
         const processNamesToAddToAllQueryAndViewScreens = materialDashboardInstanceMetaData.processNamesToAddToAllQueryAndViewScreens;
         if (processNamesToAddToAllQueryAndViewScreens)
         {
            for (let processName of processNamesToAddToAllQueryAndViewScreens)
            {
               genericProcesses.push(metaData?.processes?.get(processName));
            }
         }
      }
      else
      {
         genericProcesses.push(metaData?.processes.get("runRecordScript"));
      }
      return genericProcesses;
   }

   /////////////////////////////////////////////////////////
   // select or build the action menu from table metadata //
   /////////////////////////////////////////////////////////
   useEffect(() =>
   {
      let menu: QMenu = null;
      let additionalMenus: QMenu[] = [];
      if (metaData && tableMetaData)
      {
         menu = (tableMetaData.menus ?? []).find(m => m.slot == "QUERY_SCREEN_ACTIONS");
         additionalMenus = (tableMetaData.menus ?? []).filter(m => m.slot == "QUERY_SCREEN_ADDITIONAL");

         if (!menu)
         {
            menu = buildDefaultQueryActionMenu();
         }
      }
      setActionMenu(menu);
      setAdditionalActionMenus(additionalMenus);
   }, [metaData, tableMetaData]);

   /////////////////////////////////////////////////////////
   // action handlers for the metadata-driven action menu //
   /////////////////////////////////////////////////////////
   const queryMenuActions: ActionMenuActions = {
      bulkLoad: () => bulkLoadClicked(),
      bulkEdit: () => bulkEditClicked(),
      bulkEditWithFile: () => bulkEditWithFileClicked(),
      bulkDelete: () => bulkDeleteClicked(),
      developerMode: () => navigate(`${metaData.getTablePathByName(tableName)}/dev`),
      runProcess: (processName: string) =>
      {
         const process = metaData?.processes?.get(processName);
         if (process)
         {
            processClicked(process);
         }
         else
         {
            console.warn(`Cannot launch process [${processName}] - not found in metaData (likely a permissions issue).`);
         }
      },
      getMetaData: () => metaData,
      getTableProcesses: () => tableProcesses,
      getGenericProcesses: () => getGenericProcesses(metaData),
   };

   //////////////////////
   // ole' faithful... //
   //////////////////////
   const [, forceUpdate] = useReducer((x) => x + 1, 0);

   ///////////////////////////////////////////////////////////////////////////////////
   // row-edit hook + derived context value + EditableRow component (memoised)      //
   // placed after all state declarations so the closures capture the correct vars  //
   ///////////////////////////////////////////////////////////////////////////////////
   const rowEditHostId = useId();
   const rowEditor = useRowEditor({
      tableMetaData,
      editingRow,
      editingRowId,
      hostId: rowEditHostId,
      onFormDisabled: setErrorAlert,
      ////////////////////////////////////////////////////////////////////////////////
      // Mirror the join-list logic the main query path uses (see "figure out joins //
      // to use in the query" below) so the row-editor's post-save refetch returns  //
      // the same join values the grid renders — without these, makeRows([saved])   //
      // produces blanks for join-backed cells until the next full query runs.      //
      // Lazy: getVisibleJoinTables / ensureOrderBysFromJoinTablesAreVisibleTables  //
      // are defined further down in this component, so we only invoke them at      //
      // refetch time (well after first render, by which point everything's bound). //
      ////////////////////////////////////////////////////////////////////////////////
      getRefetchQueryJoins: () =>
      {
         if (!tableMetaData?.exposedJoins) return null;
         const visibleJoinTables = getVisibleJoinTables();
         const tablesToAdd = ensureOrderBysFromJoinTablesAreVisibleTables(queryFilter, visibleJoinTables);
         tablesToAdd?.forEach(t => visibleJoinTables.add(t));
         return TableUtils.getQueryJoins(tableMetaData, visibleJoinTables);
      },
      getRefetchTableVariant: () => tableVariant ?? null,
      onSaveComplete: (saved) =>
      {
         ////////////////////////////////////////////////////////////////////////////////
         // Re-build the flat grid row from the refreshed QRecord using DataGridUtils. //
         ////////////////////////////////////////////////////////////////////////////////
         const refreshedRows = DataGridUtils.makeRows([saved], tableMetaData, tableVariant);
         const refreshedRow = refreshedRows.length > 0 ? refreshedRows[0] : null;

         ////////////////////////////////////////////////////////////////////////////////
         // the refetch is a GET, which never selects on-request virtual fields, so    //
         // those cells would go blank until the next full query.  an inline edit      //
         // cannot change them (they summarize other records), so keep the old values. //
         ////////////////////////////////////////////////////////////////////////////////
         const onRequestFieldNames = getVirtualFieldNamesToInclude();
         ///////////////////////////////////////////////////////////////////////////////
         // Force the row-data update to commit BEFORE the Formik tree unmounts, so   //
         // the display cells read the refreshed values on the very first read-only   //
         // paint.  Without flushSync, React batches setRows with setEditingRowId and //
         // DataGrid's internal row-model sees the new data a frame too late — the    //
         // display cells flash the OLD value before the refreshed one pops in.       //
         //                                                                           //
         // ALSO update latestQueryResults (raw QRecord list): rowEditOnEnterEdit     //
         // reads from that on re-entry, so a stale record here causes the second     //
         // edit to seed Formik with pre-save values.                                 //
         ///////////////////////////////////////////////////////////////////////////////
         const rowIdToUpdate = editingRowId;
         if (refreshedRow)
         {
            flushSync(() =>
            {
               setRows((prev: any[]) => prev.map((r: any) =>
               {
                  if (r.__rowIndex === rowIdToUpdate)
                  {
                     return {...TableUtils.carryOverOnRequestValues(r, refreshedRow, onRequestFieldNames), __rowIndex: r.__rowIndex};
                  }
                  return r;
               }));
            });
         }
         setLatestQueryResults((prev: QRecord[]) =>
         {
            if (!prev) return prev;
            const copy = [...prev];
            const idx = typeof rowIdToUpdate === "number" ? rowIdToUpdate : Number(rowIdToUpdate);
            if (!Number.isNaN(idx) && idx >= 0 && idx < copy.length)
            {
               TableUtils.carryOverOnRequestRecordValues(copy[idx], saved, onRequestFieldNames);
               copy[idx] = saved;
            }
            return copy;
         });
         const restoreScroll = captureGridScroll(gridApiRef);
         setEditingRowId(null);
         setEditingRow(null);
         /////////////////////////////////////////////////////////////////////////////
         // DataGrid caches the auto-measured height from the editing row.  Without //
         // resetRowHeights(), successive edits of the same row stack the cached    //
         // heights and the row grows taller every time.                            //
         /////////////////////////////////////////////////////////////////////////////
         gridApiRef.current?.resetRowHeights?.();
         restoreScroll();
      },
      onCancel: () =>
      {
         const restoreScroll = captureGridScroll(gridApiRef);
         setEditingRowId(null);
         setEditingRow(null);
         gridApiRef.current?.resetRowHeights?.();
         restoreScroll();
      },
   });

   const rowEditRequestSave = useCallback(() => formikSubmitRef.current?.(), []);
   const rowEditRequestCancel = useCallback(() =>
   {
      const restoreScroll = captureGridScroll(gridApiRef);
      setEditingRowId(null);
      setEditingRow(null);
      gridApiRef.current?.resetRowHeights?.();
      restoreScroll();
   }, [gridApiRef]);

   //////////////////////////////////////////////////////////////////////////////////////
   // rows a user expanded through a multi-line cell; such rows get auto height below, //
   // and the set clears whenever the rows themselves change (new page, new query)     //
   //////////////////////////////////////////////////////////////////////////////////////
   const expandedGridRowsContextValue: ExpandedGridRows = useMemo(() => ({
      isProvided: true,
      expandedRowIds: expandedRowIds,
      toggleRow: (rowId: GridRowId) => setExpandedRowIds((previous) =>
      {
         const next = new Set(previous);
         if (next.has(rowId))
         {
            next.delete(rowId);
         }
         else
         {
            next.add(rowId);
         }
         return (next);
      }),
      rows: (rows ?? []).map((row: any) => ({id: row.__rowIndex as GridRowId, values: row})),
      setRowsExpanded: (rowIds: GridRowId[], expanded: boolean) => setExpandedRowIds((previous) =>
      {
         const next = new Set(previous);
         rowIds.forEach((rowId) => (expanded ? next.add(rowId) : next.delete(rowId)));
         return (next);
      }),
   }), [expandedRowIds, rows]);

   ////////////////////////////////////////////////////////////////////////////////////
   // re-measure on expand/collapse the same way the row banner does: now and on the //
   // next two frames, because the grid only re-measures an "auto" row once its      //
   // content has actually painted taller (see the banner effect above)              //
   ////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      gridApiRef.current?.resetRowHeights?.();
      const frame1 = requestAnimationFrame(() =>
      {
         gridApiRef.current?.resetRowHeights?.();
         requestAnimationFrame(() =>
         {
            gridApiRef.current?.resetRowHeights?.();
         });
      });

      ///////////////////////////////////////////////////////////////////////////////////
      // a row that just collapsed still has its resize observer attached for one more //
      // measurement, which lands after these frames and would pin the row at its      //
      // shrunken content height - so measure once more after the observer has settled //
      ///////////////////////////////////////////////////////////////////////////////////
      const settle = setTimeout(() => gridApiRef.current?.resetRowHeights?.(), 250);
      return () =>
      {
         cancelAnimationFrame(frame1);
         clearTimeout(settle);
      };
   }, [expandedRowIds, gridApiRef]);

   const rowEditContextValue: RowEditContextValue = useMemo(() => ({
      editingRowId,
      savedRowId: rowEditor.savedRowId,
      formFieldDefs: rowEditor.formFieldDefs,
      isSaving: rowEditor.isSaving,
      rowBannerState: rowEditor.rowBannerState,
      requestSave: rowEditRequestSave,
      requestCancel: rowEditRequestCancel,
      handleFieldBlur: rowEditor.handleFieldBlur,
   }), [editingRowId, rowEditor.savedRowId, rowEditor.formFieldDefs, rowEditor.isSaving, rowEditor.rowBannerState, rowEditRequestSave, rowEditRequestCancel, rowEditor.handleFieldBlur]);

   ///////////////////////////////////////////////////////////////////////////////////////////
   // inline row-editing is a query-screen-only affordance - in the modal usages            //
   // (recordPicker, reportSetup), rows must not be editable through the hover pencil.      //
   ///////////////////////////////////////////////////////////////////////////////////////////
   const rowEditCanEdit = usage == "queryScreen" && tableMetaData?.capabilities.has(Capability.TABLE_UPDATE) && tableMetaData?.editPermission;

   //////////////////////////////////////////////////////////////////////////////////
   // single-select picker - one row at a time, drawn with radios.  select-all and //
   // subset selection are multi-select concepts and are hidden in this mode.      //
   //////////////////////////////////////////////////////////////////////////////////
   const isSingleSelectPicker = usage == "recordPicker" && selectionMode == "single";

   /////////////////////////////////////////////////////////////////////////////////////
   // the actual locked criteria - the leading criteria of the filter we were opened  //
   // with.  captured once, so they can be re-asserted after anything that would drop //
   // them (Reset All Changes, picking a saved view, New View, clearing filters...).  //
   //                                                                                 //
   // "once" means the first render where the filter is actually THERE, not the first //
   // render full stop: a host that renders RecordQuery before its filter resolves    //
   // would otherwise capture nothing, leaving lockedCriteria empty while             //
   // lockedCriteriaCount is positive - the leading rows render read-only, but        //
   // doSetQueryFilter never re-asserts them.                                         //
   /////////////////////////////////////////////////////////////////////////////////////
   ////////////////////////////////////////////////////////////////////////////////////////
   // deep-cloned, for the same reason the initial filter is at "first render" below:    //
   // a bare slice copies the array but keeps the CALLER's criterion objects, and        //
   // doSetQueryFilter installs those very objects into the live queryFilter - where     //
   // CustomFilterPanel assigns react-key ids onto them and FilterCriteriaRow rewrites   //
   // an operator the field does not offer.  Those writes would land in the host's own   //
   // data (a DynamicSelect's sourceTable.filter, a widget's frontendQueryFilter).       //
   //                                                                                    //
   // Safe against the duplicate-accumulation this once caused, because doSetQueryFilter //
   // matches locked criteria on a normalized {fieldName, operator, values} rather than  //
   // on object identity.                                                                //
   ////////////////////////////////////////////////////////////////////////////////////////
   const lockedCriteriaRef = useRef<QFilterCriteria[]>(null);
   if (lockedCriteriaRef.current === null && lockedCriteriaCount > 0 && initialQueryFilter?.criteria?.length > 0)
   {
      lockedCriteriaRef.current = JSON.parse(JSON.stringify(initialQueryFilter.criteria.slice(0, lockedCriteriaCount)));
   }
   const lockedCriteria = lockedCriteriaRef.current ?? [];

   //////////////////////////////////////////////////////////////////////////////////////
   // whether the filter in play says Or while the locked criteria are being enforced  //
   // as And.  Read by the filter panel, so the caption there can tell the user their  //
   // Or is being treated as an And.  The filter itself KEEPS that Or (the And is      //
   // applied only to the filters sent to the backend - see                            //
   // prepQueryFilterForBackendEnforcingLockedCriteria), so what the report / widget   //
   // filter editor saves back onto the record is unchanged by the enforcement, and    //
   // this flag is a disclosure of how the results were computed.                      //
   //                                                                                  //
   // A ref rather than state: doSetQueryFilter is reached from the filter panel's own //
   // render body, where scheduling a render on this component logs a React warning.   //
   // The flag is set on the activateView that loads the filter, long before the panel //
   // can be opened, so the panel's render reads a settled value.                      //
   //////////////////////////////////////////////////////////////////////////////////////
   const lockedCriteriaIsOverridingBooleanOperator = useRef<boolean>(false);

   /*******************************************************************************
    ** prep the given filter for the backend, forcing And whenever the picker's
    ** locked criteria are in play - the one place that lock on the And/Or operator
    ** is actually enforced.
    *******************************************************************************/
   const prepQueryFilterForBackendEnforcingLockedCriteria = (filter: QQueryFilter, pageNumber?: number, rowsPerPage?: number): QQueryFilter =>
   {
      const filterForBackend = FilterUtils.prepQueryFilterForBackend(tableMetaData, filter, pageNumber, rowsPerPage);

      //////////////////////////////////////////////////////////////////////////////////////
      // force the And HERE, where the filter is used to query, rather than by writing it //
      // onto the filter object.  With an Or, a locked criterion stops constraining       //
      // anything ("clientId=47 OR name contains x" matches other clients), which would   //
      // defeat the whole point of locking it - but the filter object is the very one the //
      // report / widget filter editor hands back out of getCurrentView() and saves onto  //
      // the record, so writing And into it PERSISTED the change.  And it happened on     //
      // load, so a user who opened the editor, changed only a column and clicked OK      //
      // silently stored a different operator on a report they never edited.              //
      //                                                                                  //
      // Enforcing at query time leaves the stored filter's own operator alone, and holds //
      // for exactly as long as the criteria are locked.                                  //
      //////////////////////////////////////////////////////////////////////////////////////
      if (lockedCriteria.length > 0)
      {
         filterForBackend.booleanOperator = "AND";
      }

      return (filterForBackend);
   };

   const rowEditOnEnterEdit = useCallback((rowId: string | number) =>
   {
      /////////////////////////////////////////////////////////////////////////////////
      // Use the QRecord from latestQueryResults (raw values) rather than the flat   //
      // grid-row object, which contains DISPLAY values (e.g. "Wednesday" instead of //
      // the numeric dayOfWeekId).  Initial form values MUST be raw values so the    //
      // save payload round-trips correctly to the backend.                          //
      /////////////////////////////////////////////////////////////////////////////////
      const index = typeof rowId === "number" ? rowId : Number(rowId);
      const rec = (latestQueryResults && !isNaN(index)) ? latestQueryResults[index] : null;
      if (!rec) return;
      //////////////////////////////////////////////////////////////////////////////
      // Clear any lingering row-edit page-level error from a prior form-disabled //
      // bail-out so a fresh edit session starts clean.                           //
      //////////////////////////////////////////////////////////////////////////////
      setErrorAlert(null);
      setEditingRowId(rowId);
      setEditingRow(rec);
   }, [latestQueryResults]);

   const EditableRowComponent = useMemo(() => makeEditableRow({
      initialValues: rowEditor.initialValues,
      validationSchema: rowEditor.validationSchema,
      onSubmit: rowEditor.handleSubmit,
      onFieldChange: rowEditor.onFieldChange,
      formValuesRef: rowEditor.formValuesRef,
      formValuesJSONRef: rowEditor.formValuesJSONRef,
      setFieldValueRef: rowEditor.setFieldValueRef,
      formikSubmitRef,
      setRowBannerState: rowEditor.setRowBannerState,
   }), [rowEditor.initialValues, rowEditor.validationSchema, rowEditor.setRowBannerState]);

   ///////////////////////////////////////////////////////////////////////////////////////////////
   // bump column widths to editable minimums once formFieldDefs are populated for the edit row //
   ///////////////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (editingRowId != null && Object.keys(rowEditor.formFieldDefs).length > 0)
      {
         /////////////////////////////////////////////////////////////////////////////////////
         // Augment columnVisibilityModel so that any required+editable fields the user has //
         // hidden become visible for the duration of this edit.  Without this, a required  //
         // field with a missing value would block save with a Formik error the user has no //
         // way to see or fix (the cell is not in the grid).  Restored on edit-exit via the //
         // pre-edit snapshot.  The view persistence side effects (queryColumns,            //
         // doSetView, forceUpdate) are intentionally skipped here so the user's saved view //
         // is not modified by the temporary augmentation.                                  //
         /////////////////////////////////////////////////////////////////////////////////////
         let effectiveVisibilityModel = columnVisibilityModel;
         const augmented: GridColumnVisibilityModel = {...columnVisibilityModel};
         let needsAugment = false;
         for (const fieldName in rowEditor.formFieldDefs)
         {
            const def = rowEditor.formFieldDefs[fieldName];
            if (def.isEditable && def.isRequired && augmented[fieldName] === false)
            {
               augmented[fieldName] = true;
               needsAugment = true;
            }
         }
         if (needsAugment)
         {
            if (preEditColumnVisibilityModelRef.current === null)
            {
               preEditColumnVisibilityModelRef.current = {...columnVisibilityModel};
            }
            isAdjustingColumnVisibilityForEditRef.current = true;
            setColumnVisibilityModel(augmented);
            effectiveVisibilityModel = augmented;
            //////////////////////////////////////////////////////////////////////////////
            // Clear the flag after a few frames so any MUI-internal onChange callbacks //
            // fired in response to the augmentation are still gated.  Two rAFs is      //
            // enough for MUI's effect-driven event dispatch in our normal render path. //
            //////////////////////////////////////////////////////////////////////////////
            requestAnimationFrame(() => requestAnimationFrame(() =>
            {
               isAdjustingColumnVisibilityForEditRef.current = false;
            }));
         }

         ////////////////////////////////////////////////////////////////////////////////////
         // Capture first-visible column, flushSync the width bump so React commits in the //
         // same synchronous block, then correct scrollLeft.  Browser paints once with the //
         // widths AND the scroll adjustment — no flicker to the shifted intermediate.     //
         ////////////////////////////////////////////////////////////////////////////////////
         const restoreHScroll = preserveFirstVisibleColumn(gridApiRef);
         flushSync(() =>
         {
            bumpColumnWidthsForEdit(gridApiRef, rowEditor.formFieldDefs, effectiveVisibilityModel);
         });
         restoreHScroll();
      }
      ////////////////////////////////////////////////////////////////////////////////
      // columnVisibilityModel is in the dep list so that when we augment it for    //
      // required fields above, the effect re-fires AFTER MUI has rendered with the //
      // new model — the second run widens the newly-visible columns (MUI ignores   //
      // setColumnWidth on columns it still considers hidden at the time of call).  //
      ////////////////////////////////////////////////////////////////////////////////
   }, [editingRowId, rowEditor.formFieldDefs, columnVisibilityModel, gridApiRef]);

   /////////////////////////////////////////////////////////////////////////////////
   // Restore the user's pre-edit columnVisibilityModel when an edit ends.        //
   // Pairs with the augment-on-entry in the effect above; runs on save, cancel,  //
   // or any other path that clears editingRowId.                                 //
   /////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (editingRowId == null && preEditColumnVisibilityModelRef.current !== null)
      {
         const restoreModel = preEditColumnVisibilityModelRef.current;
         preEditColumnVisibilityModelRef.current = null;
         isAdjustingColumnVisibilityForEditRef.current = true;
         setColumnVisibilityModel(restoreModel);
         requestAnimationFrame(() => requestAnimationFrame(() =>
         {
            isAdjustingColumnVisibilityForEditRef.current = false;
         }));
      }
   }, [editingRowId]);

   //////////////////////////////////////////////////////////////////////////////////
   // When the row banner transitions into or out of a non-"none" kind, force the  //
   // grid to re-measure row heights.  Without this, a "saveError" banner appearing //
   // mid-edit is absolutely-positioned on top of the next row because DataGrid     //
   // has cached the pre-banner (shorter) auto-height for the editing row.         //
   //                                                                              //
   // The banner text renders in a portal and EditableRow's inner spacer resizes    //
   // via its ResizeObserver a frame or two AFTER the bannerState flip.  We thus    //
   // trigger multiple resets: immediately (catches the getRowHeight return change) //
   // and on the next rAF passes (catches the spacer growth once the portal content //
   // has painted).                                                                 //
   //////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (editingRowId == null) return;
      gridApiRef.current?.resetRowHeights?.();
      const frame1 = requestAnimationFrame(() =>
      {
         gridApiRef.current?.resetRowHeights?.();
         requestAnimationFrame(() =>
         {
            gridApiRef.current?.resetRowHeights?.();
         });
      });
      return () => cancelAnimationFrame(frame1);
   }, [rowEditor.rowBannerState.kind, editingRowId, gridApiRef]);

   //////////////////////////////////////////////////////////////////
   // we use our own header - so clear out the context page header //
   //////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (!isModal)
      {
         setPageHeader(null);
      }
   }, [isModal, setPageHeader]);

   ///////////////////////////////////////////////////////////////////////////////////////////
   // add a LoadingState object, in case the initial loads (of meta data and view) are slow //
   ///////////////////////////////////////////////////////////////////////////////////////////
   const [pageLoadingState, _] = useState(new LoadingState(forceUpdate));

   /////////////////////////////////////////////////////////////////////////////////
   // use this to make changes to the queryFilter more likely to re-run the query //
   /////////////////////////////////////////////////////////////////////////////////
   const [filterHash, setFilterHash] = useState("");

   /////////////////////////////////////////////////////////////////////////////////////////////////
   // handle first render after changing tables - reset state based on defaults from localStorage //
   /////////////////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (isFirstRenderAfterChangingTables)
      {
         setIsFirstRenderAfterChangingTables(false);
         console.log("This is the first render after changing tables - so - setting state based on 'defaults' from localStorage");
         setView(defaultView);
      }
   }, [isFirstRenderAfterChangingTables, defaultView]);

   ////////////////////////////////////////////////////////////////////////
   // trigger initial update-table call after page-state goes into ready //
   ////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (pageState == "ready")
      {
         pageLoadingState.setNotLoading();

         if (!tableVariantPromptOpen)
         {
            updateTable("pageState is now ready");
         }
      }
   }, [pageState, tableVariantPromptOpen]);

   /*******************************************************************************
    ** utility function to get the names of any join tables which are active,
    ** either as a visible column, or as a query criteria
    *******************************************************************************/
   const getVisibleJoinTables = (): Set<string> =>
   {
      const visibleJoinTables = new Set<string>();

      for (let i = 0; i < queryColumns?.columns.length; i++)
      {
         const column = queryColumns.columns[i];
         const fieldName = column.name;
         if (column.isVisible && fieldName.indexOf(".") > -1)
         {
            visibleJoinTables.add(fieldName.split(".")[0]);
         }
      }

      for (let i = 0; i < queryFilter?.criteria?.length; i++)
      {
         const criteria = queryFilter.criteria[i];
         const {criteriaIsValid} = validateCriteria(criteria, null);
         const fieldName = criteria.fieldName;
         if (criteriaIsValid && fieldName && fieldName.indexOf(".") > -1)
         {
            visibleJoinTables.add(fieldName.split(".")[0]);
         }
      }

      return (visibleJoinTables);
   };

   /*******************************************************************************
    ** names of the visible columns the backend only computes on request (virtual
    ** fields with includeInQueryByDefault=false) - sent with every query, see updateTable
    *******************************************************************************/
   const getVirtualFieldNamesToInclude = (): string[] =>
   {
      return (TableUtils.getVirtualFieldNamesToInclude(tableMetaData, queryColumns?.columns));
   };

   /*******************************************************************************
    **
    *******************************************************************************/
   const isJoinMany = (tableMetaData: QTableMetaData, visibleJoinTables: Set<string>): boolean =>
   {
      if (tableMetaData?.exposedJoins)
      {
         for (let i = 0; i < tableMetaData.exposedJoins.length; i++)
         {
            const join = tableMetaData.exposedJoins[i];
            if (visibleJoinTables.has(join.joinTable.name))
            {
               if (join.isMany)
               {
                  return (true);
               }
            }
         }
      }
      return (false);
   };


   /*******************************************************************************
    **
    *******************************************************************************/
   function openExportMenu(event: any)
   {
      if (!metaData.tables?.get(tableMetaData.name)?.capabilities?.has(Capability.TABLE_EXPORT))
      {
         setAlertContent("Exports are not allowed for this table.");
         return;
      }

      setExportMenuAnchorElement(event.currentTarget);
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function closeExportMenu()
   {
      setExportMenuAnchorElement(null);
   }


   ///////////////////////////////////////////
   // build the export menu, for the header //
   ///////////////////////////////////////////
   let exportMenu = <></>;
   try
   {
      const exportMenuItemRestProps =
         {
            tableMetaData: tableMetaData,
            totalRecords: totalRecords,
            columnsModel: columnsModel,
            columnVisibilityModel: columnVisibilityModel,
            queryFilter: prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter)
         };

      exportMenu = (<>
         <IconButton sx={{p: 0, fontSize: "0.75rem", mb: 1, color: colors.secondary.main, fontVariationSettings: "'wght' 100"}} onClick={openExportMenu}><Icon fontSize="small">save_alt</Icon></IconButton>
         <Menu
            anchorEl={exportMenuAnchorElement}
            anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            transformOrigin={{vertical: "top", horizontal: "center"}}
            open={exportMenuAnchorElement != null}
            onClose={closeExportMenu}
            sx={{top: "0.5rem"}}
            keepMounted>
            <ExportMenuItem format="csv" {...exportMenuItemRestProps} />
            <ExportMenuItem format="xlsx" {...exportMenuItemRestProps} />
            <ExportMenuItem format="json" {...exportMenuItemRestProps} />
         </Menu>
      </>);
   }
   catch (e)
   {
      console.log("Error preparing export menu for page header: " + e);
   }

   /*******************************************************************************
    **
    *******************************************************************************/
   const getPageHeader = (tableMetaData: QTableMetaData, visibleJoinTables: Set<string>, tableVariant: QTableVariant): string | JSX.Element =>
   {
      let label: string = tableMetaData?.label ?? "";

      if (currentSavedView?.values?.get("label"))
      {
         label += " / " + currentSavedView?.values?.get("label");
      }

      if (visibleJoinTables.size > 0)
      {
         let joinLabels = [];
         if (tableMetaData?.exposedJoins)
         {
            for (let i = 0; i < tableMetaData.exposedJoins.length; i++)
            {
               const join = tableMetaData.exposedJoins[i];
               if (visibleJoinTables.has(join.joinTable.name))
               {
                  joinLabels.push(join.label);
               }
            }
         }

         let joinLabelsString = joinLabels.join(", ");
         if (joinLabels.length == 2)
         {
            let lastCommaIndex = joinLabelsString.lastIndexOf(",");
            joinLabelsString = joinLabelsString.substring(0, lastCommaIndex) + " and " + joinLabelsString.substring(lastCommaIndex + 1);
         }
         if (joinLabels.length > 2)
         {
            let lastCommaIndex = joinLabelsString.lastIndexOf(",");
            joinLabelsString = joinLabelsString.substring(0, lastCommaIndex) + ", and " + joinLabelsString.substring(lastCommaIndex + 1);
         }

         let tooltipHTML = <div>
            You are viewing results from the {tableMetaData.label} table joined with {joinLabels.length} other table{joinLabels.length == 1 ? "" : "s"}:
            <ul style={{marginLeft: "1rem"}}>
               {joinLabels.map((name) => <li key={name}>{name}</li>)}
            </ul>
         </div>;

         return (
            <div>
               {label} {exportMenu}
               <CustomWidthTooltip title={tooltipHTML}>
                  <IconButton sx={{ml: "0.5rem", p: 0, fontSize: "0.5rem", mb: 1, color: "#9f9f9f", fontVariationSettings: "'wght' 100"}}><Icon fontSize="small">emergency</Icon></IconButton>
               </CustomWidthTooltip>
               {tableVariant && getTableVariantHeader(tableVariant)}
            </div>);
      }
      else
      {
         return (
            <div>
               {label} {exportMenu}
               {tableVariant && getTableVariantHeader(tableVariant)}
            </div>);
      }
   };

   /*******************************************************************************
    ** Render the variant label.  When the host has forced the variant, the
    ** "change" gear is suppressed — user cannot pick a different one from
    ** inside the embedded query.
    *******************************************************************************/
   const getTableVariantHeader = (tableVariant: QTableVariant) =>
   {
      return (
         <Typography variant="h6" color="text" fontWeight="light">
            {tableMetaData?.variantTableLabel}: {tableVariant?.name}
            {!forcedTableVariant && (
               <Tooltip title={`Change ${tableMetaData?.variantTableLabel}`}>
                  <IconButton onClick={promptForTableVariantSelection} sx={{p: 0, m: 0, ml: .5, mb: .5, color: "#9f9f9f", fontVariationSettings: "'weight' 100"}}><Icon fontSize="small">settings</Icon></IconButton>
               </Tooltip>
            )}
         </Typography>
      );
   };

   ///////////////////////
   // Keyboard handling //
   ///////////////////////
   useEffect(() =>
   {
      const down = (e: KeyboardEvent) =>
      {
         const type = (e.target as any).type;
         const validType = (type !== "text" && type !== "textarea" && type !== "input" && type !== "search");

         if (validType && !isModal && !dotMenuOpen && !keyboardHelpOpen && !activeModalProcess && (!modalStack || modalStack.length == 0))
         {
            if (!e.metaKey && !e.ctrlKey && e.key === "n" && table.capabilities.has(Capability.TABLE_INSERT) && table.insertPermission)
            {
               e.preventDefault();
               navigate(`${metaData?.getTablePathByName(tableName)}/create`);
            }
            else if (!e.metaKey && !e.ctrlKey && e.key === "r")
            {
               e.preventDefault();
               updateTable("'r' keyboard event");
            }
            ////////////////////////////////////////////////////////////////////////////////////////
            // disable until we add a ... ref down to let us programmatically open Columns button //
            // else if (! e.metaKey && !e.ctrlKey && e.key === "c")                               //
            // {                                                                                  //
            // e.preventDefault()                                                                 //
            // gridApiRef.current.showPreferences(GridPreferencePanelsValue.columns)              //
            // }                                                                                  //
            ////////////////////////////////////////////////////////////////////////////////////////
            else if (!e.metaKey && !e.ctrlKey && e.key === "f")
            {
               e.preventDefault();

               ////////////////
               // @ts-ignore //
               ////////////////
               if (basicAndAdvancedQueryControlsRef?.current?.getCurrentMode() == "advanced")
               {
                  gridApiRef.current.showFilterPanel();
               }
            }
            else if (!e.metaKey && !e.ctrlKey && e.key === ",")
            {
               e.preventDefault();
               const columnsButton = document.querySelector("[data-qqq-id=\"button-columns\"]") as HTMLElement | null;
               columnsButton?.click();
            }
         }
      };

      document.addEventListener("keydown", down);
      return () =>
      {
         document.removeEventListener("keydown", down);
      };
   }, [isModal, dotMenuOpen, keyboardHelpOpen, metaData, activeModalProcess, modalStack]);


   /*******************************************************************************
    **
    *******************************************************************************/
   const urlLooksLikeProcess = (): boolean =>
   {
      return (pathParts[pathParts.length - 2] === tableName);
   };

   //////////////////////////////////////////////////////////////////////////////////////////////
   // monitor location changes - if our url looks like a savedView, then load that view, kinda //
   //////////////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      ///////////////////////////////////////////////////////////////////////////////////
      // the location belongs to the HOST page, so none of this applies to the modal   //
      // usages.  A picker over orderLine rendered while the host page sits on         //
      // app/orders/savedView/32 would otherwise setViewIdInLocation(32) and load      //
      // order's saved view into a query over a different table.  (The navigate in the //
      // else-branch below was already gated on usage; the rest was not.)              //
      ///////////////////////////////////////////////////////////////////////////////////
      if (usage != "queryScreen")
      {
         return;
      }

      try
      {
         /////////////////////////////////////////////////////////////////
         // the path for a savedView looks like: .../table/savedView/32 //
         // so if path has '/savedView/' get last parsed string         //
         /////////////////////////////////////////////////////////////////
         let currentSavedViewId = null as number;
         if (location.pathname.indexOf("/savedView/") != -1)
         {
            const parts = location.pathname.split("/");
            currentSavedViewId = Number.parseInt(parts[parts.length - 1]);
            setViewIdInLocation(currentSavedViewId);

            /////////////////////////////////////////////////////////////////////////////////////////////
            // in case page-state has already advanced to "ready" (e.g., and we're dealing with a user //
            // hitting back & forth between filters), then do a load of the new saved-view right here  //
            /////////////////////////////////////////////////////////////////////////////////////////////
            if (pageState == "ready")
            {
               handleSavedViewChange(currentSavedViewId);
            }
         }
         else if (!searchParams.has("filter"))
         {
            if (localStorage.getItem(currentSavedViewLocalStorageKey))
            {
               if (usage == "queryScreen")
               {
                  currentSavedViewId = Number.parseInt(localStorage.getItem(currentSavedViewLocalStorageKey));
                  navigate(`${metaData.getTablePathByName(tableName)}/savedView/${currentSavedViewId}`);
               }
            }
            else
            {
               doClearCurrentSavedView();
            }
         }
      }
      catch (e)
      {
         console.log(e);
      }
   }, [location]);


   /*******************************************************************************
    ** set the current view in state & local-storage - but do NOT update any
    ** child-state data.
    *******************************************************************************/
   const doSetView = (view: RecordQueryView): void =>
   {
      setView(view);
      const viewAsJSON = JSON.stringify(view);
      setViewAsJson(viewAsJSON);

      try
      {
         ////////////////////////////////////////////////////////////////////////////////////
         // in case there's an incomplete criteria in the view (e.g., w/o a fieldName),    //
         // don't store that in local storage - we don't want that, it's messy, and it     //
         // has caused fails in the past.  So, clone the view, and strip away such things. //
         ////////////////////////////////////////////////////////////////////////////////////
         const viewForLocalStorage: RecordQueryView = JSON.parse(viewAsJSON);
         if (viewForLocalStorage?.queryFilter?.criteria?.length > 0)
         {
            FilterUtils.stripAwayIncompleteCriteria(viewForLocalStorage.queryFilter);
         }
         localStorageSet(viewLocalStorageKey, JSON.stringify(viewForLocalStorage));
      }
      catch (e)
      {
         console.log("Error storing view in local storage: " + e);
      }
   };


   /*******************************************************************************
    **
    *******************************************************************************/
   const handleColumnVisibilityChange = (columnVisibilityModel: GridColumnVisibilityModel) =>
   {
      /////////////////////////////////////////////////////////////////////////////
      // Skip the edit-cancel branch when we are driving the visibility change   //
      // ourselves (force-showing required fields at edit-entry, restoring at    //
      // edit-exit).  User-driven toggles in the columns chooser go through      //
      // handleChangeOneColumnVisibility, not here, so this gate does not affect //
      // the intentional "user hid a column during edit → cancel" UX.            //
      /////////////////////////////////////////////////////////////////////////////
      if (editingRowId != null && !isAdjustingColumnVisibilityForEditRef.current)
      {
         setEditingRowId(null);
         setEditingRow(null);
      }

      setColumnVisibilityModel(columnVisibilityModel);
      queryColumns.updateVisibility(columnVisibilityModel);

      view.queryColumns = queryColumns;
      doSetView(view);

      forceUpdate();
   };


   /*******************************************************************************
    ** function called by columns menu to turn a column on or off
    *******************************************************************************/
   const handleChangeOneColumnVisibility = (field: QFieldMetaData, table: QTableMetaData, newValue: boolean) =>
   {
      if (editingRowId != null)
      {
         setEditingRowId(null);
         setEditingRow(null);
      }

      ///////////////////////////////////////
      // set the field's value in the view //
      ///////////////////////////////////////
      let fieldName = field.name;
      if (table && table.name != tableMetaData.name)
      {
         fieldName = `${table.name}.${field.name}`;
      }

      view.queryColumns.setIsVisible(fieldName, newValue);

      /////////////////////
      // update the grid //
      /////////////////////
      setColumnVisibilityModel(queryColumns.toColumnVisibilityModel());

      /////////////////////////////////////////////////
      // update the view (e.g., write local storage) //
      /////////////////////////////////////////////////
      doSetView(view);

      ///////////////////
      // ole' faithful //
      ///////////////////
      forceUpdate();
   };


   /*******************************************************************************
    ** Click handler for the "locate column" end-adornment in the Columns menu.
    ** Unhides the column if hidden, scrolls it into view, and triggers a brief
    ** pulse on the header + cells so the user can see where it landed.
    *******************************************************************************/
   const locateColumnInGrid = (field: QFieldMetaData, table: QTableMetaData) =>
   {
      const fullFieldName =
         table && table.name !== tableMetaData.name
            ? `${table.name}.${field.name}`
            : field.name;

      const currentToggleStates = view.queryColumns.getVisibilityToggleStates();
      if (!currentToggleStates[fullFieldName])
      {
         handleChangeOneColumnVisibility(field, table, true);
      }

      ///////////////////////////////////////////////////////////////////////////////////
      // pinned columns are always on-screen; scrollToIndexes mis-computes for them    //
      // (scrolls all the way to the edge), so skip the scroll and just pulse in place //
      ///////////////////////////////////////////////////////////////////////////////////
      const isPinned = (pinnedColumns?.left ?? []).includes(fullFieldName)
                    || (pinnedColumns?.right ?? []).includes(fullFieldName);

      ///////////////////////////////////////////////////////////////////////////////
      // double rAF: the first lets React commit the visibility flip and MUI       //
      // process the new columnVisibilityModel prop; the second lets MUI measure   //
      // and lay out a newly-visible column before scrollToIndexes reads its width //
      ///////////////////////////////////////////////////////////////////////////////
      requestAnimationFrame(() =>
      {
         requestAnimationFrame(() =>
         {
            const api = gridApiRef.current;
            if (!api)
            {
               return;
            }

            if (!isPinned)
            {
               ////////////////////////////////////////////////////////////////////////////////
               // scrollToIndexes uses an index into visibleColumns (not getAllColumns), so  //
               // an index from getAllColumns() can land past the end of visibleColumns when //
               // a hidden column is in the mix - causing scrollToIndexes to crash trying to //
               // read .computedWidth on undefined. Use the visible-columns array as the     //
               // index domain.                                                              //
               ////////////////////////////////////////////////////////////////////////////////
               const cols = api.getVisibleColumns();
               const colIndex = cols.findIndex((c) => c.field === fullFieldName);
               if (colIndex < 0)
               {
                  return;
               }
               api.scrollToIndexes({colIndex});
            }

            setPulsingField(fullFieldName);
            setTimeout(() => setPulsingField(null), 1000);
         });
      });
   };


   /*******************************************************************************
    **
    *******************************************************************************/
   const setupGridColumnModels = (metaData: QInstance, tableMetaData: QTableMetaData, queryColumns: QQueryColumns) =>
   {
      ////////////////////////////////////////////////////////////////////////////////////////////
      // only build record links (on the pk column) for the actual query screen - in the modal  //
      // usages (recordPicker, reportSetup), such a link would navigate the page underneath the //
      // modal, unmounting it (mirrors the onRowClick gate).  null linkBase = no link cells.    //
      ////////////////////////////////////////////////////////////////////////////////////////////
      let linkBase: string = null;
      if (usage == "queryScreen")
      {
         linkBase = metaData.getTablePath(tableMetaData);
         if(linkBase)
         {
            linkBase += linkBase.endsWith("/") ? "" : "/";
         }
      }
      const columns = DataGridUtils.setupGridColumns(tableMetaData, linkBase, metaData, "alphabetical", undefined, usage != "queryScreen");

      ///////////////////////////////////////////////
      // sort columns based on queryColumns object //
      ///////////////////////////////////////////////
      const columnSortValues = queryColumns.getColumnSortValues();
      columns.sort((a: GridColDef, b: GridColDef) =>
      {
         const aIndex = columnSortValues[a.field];
         const bIndex = columnSortValues[b.field];
         return aIndex - bIndex;
      });

      ///////////////////////////////////////////////////////////////////////
      // if there are column widths (e.g., from local storage), apply them //
      ///////////////////////////////////////////////////////////////////////
      const columnWidths = queryColumns.getColumnWidths();
      for (let i = 0; i < columns.length; i++)
      {
         const width = columnWidths[columns[i].field];
         if (width)
         {
            columns[i].width = width;
         }
      }

      /////////////////////////////////////////////////////////////////////////////////////
      // wrap every column's renderCell with RowEditCellRenderer so that the editing row //
      // shows edit inputs while all other rows continue to show display values.         //
      /////////////////////////////////////////////////////////////////////////////////////
      for (const col of columns)
      {
         const originalRender = col.renderCell ?? ((params: any) => params.formattedValue ?? params.value);
         col.renderCell = (params: any) => <RowEditCellRenderer params={params} displayRenderer={originalRender} />;
      }

      ///////////////////////////////////////////////////////////////////////////////////
      // Row-edit pencil/check/cancel is rendered as a floating overlay by EditableRow //
      // (components.Row slot) — no dedicated column is added here.                    //
      ///////////////////////////////////////////////////////////////////////////////////
      setPinnedColumns(queryColumns.toGridPinnedColumns());
      setColumnVisibilityModel(queryColumns.toColumnVisibilityModel());
      setColumnsModel(columns);
   };


   /*******************************************************************************
    **
    *******************************************************************************/
   const promptForTableVariantSelection = () =>
   {
      setTableVariantPromptOpen(true);
   };


   /*******************************************************************************
    ** return array of table names that a filter's orderBys need joined, but which
    ** are not among the currently-visible join tables.  Pure - see
    ** ensureOrderBysFromJoinTablesAreVisibleTables for the version that also makes
    ** those columns visible.
    *******************************************************************************/
   const collectOrderByJoinTablesNotVisible = (queryFilter: QQueryFilter, visibleJoinTablesParam?: Set<string>): string[] =>
   {
      const rs: string[] = [];
      const vjtToUse = visibleJoinTablesParam ?? visibleJoinTables;

      for (let i = 0; i < queryFilter?.orderBys?.length; i++)
      {
         const fieldName = queryFilter.orderBys[i].fieldName;
         if (fieldName != null && fieldName.indexOf(".") > -1)
         {
            const joinTableName = fieldName.replaceAll(/\..*/g, "");
            if (!vjtToUse.has(joinTableName))
            {
               const [field, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, fieldName);
               rs.push(fieldTable.name);
            }
         }
      }

      return (rs);
   };


   /*******************************************************************************
    ** return array of table names that need ... added to query - and, as a side
    ** effect, make each of their columns visible on the grid.
    *******************************************************************************/
   const ensureOrderBysFromJoinTablesAreVisibleTables = (queryFilter: QQueryFilter, visibleJoinTablesParam?: Set<string>): string[] =>
   {
      const rs: string[] = [];
      const vjtToUse = visibleJoinTablesParam ?? visibleJoinTables;

      for (let i = 0; i < queryFilter?.orderBys?.length; i++)
      {
         const fieldName = queryFilter.orderBys[i].fieldName;
         if (fieldName != null && fieldName.indexOf(".") > -1)
         {
            const joinTableName = fieldName.replaceAll(/\..*/g, "");
            if (!vjtToUse.has(joinTableName))
            {
               const [field, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, fieldName);
               handleChangeOneColumnVisibility(field, fieldTable, true);
               rs.push(fieldTable.name);
            }
         }
      }

      return (rs);
   };


   /*******************************************************************************
    ** Opens a new query screen in a new window with the current filter
    *******************************************************************************/
   const openFilterInNewWindow = () =>
   {
      ////////////////////////////////////////////////////////////////////////////////////
      // through the locked-criteria prep like every other path that hands this filter  //
      // to a query.  Unreachable with a lock today - the button only renders under     //
      // isPreview, and the one isPreview host passes no lockedCriteriaCount - but this //
      // was the single call site that would have quietly missed the lock the moment    //
      // that changed.                                                                  //
      ////////////////////////////////////////////////////////////////////////////////////
      let filterForBackend = JSON.parse(JSON.stringify(view.queryFilter));
      filterForBackend = prepQueryFilterForBackendEnforcingLockedCriteria(filterForBackend);
      const url = `${metaData?.getTablePathByName(tableName)}?filter=${encodeURIComponent(JSON.stringify(filterForBackend))}`;
      window.open(url);
   };


   /***************************************************************************
    *
    ***************************************************************************/
   const pushWarningAlert = (newMessage: string) =>
   {
      if (warningAlertList.indexOf(newMessage) == -1)
      {
         setWarningAlertList([...warningAlertList, newMessage]);
      }
   };


   /***************************************************************************
    *
    ***************************************************************************/
   const deleteWarningAlert = (index: number) =>
   {
      warningAlertList.splice(index, 1);
      setWarningAlertList([...warningAlertList]);
   };


   /*******************************************************************************
    ** This is the method that actually executes a query to update the data in the table.
    *******************************************************************************/
   const updateTable = (reason?: string, clearOutCount = true) =>
   {
      if (pageState != "ready")
      {
         console.log(`In updateTable, but pageSate[${pageState}] is not ready, so returning with noop`);
         return;
      }

      if (tableMetaData?.usesVariants && (!tableVariant || tableVariantPromptOpen))
      {
         console.log("In updateTable, but a variant is needed, so returning with noop");
         return;
      }

      /////////////////////////////////////////////////////////////////////////////////////////////////
      // if any values in the query are of type "FilterVariableExpression", display an error showing //
      // that a backend query cannot be made because of missing values for that expression           //
      /////////////////////////////////////////////////////////////////////////////////////////////////
      setWarningAlert(null);
      for (var i = 0; i < queryFilter?.criteria?.length; i++)
      {
         for (var j = 0; j < queryFilter?.criteria[i]?.values?.length; j++)
         {
            const value = queryFilter.criteria[i].values[j];
            if (value?.type == "FilterVariableExpression")
            {
               pushWarningAlert("Cannot perform query because of a missing value for a variable.");
               setLoading(false);
               setRows([]);
               return;
            }
         }
      }

      doRecordAnalytics({category: "tableEvents", action: "query", label: tableMetaData.label});

      console.log(`In updateTable for ${reason} ${JSON.stringify(queryFilter)}`);
      setLoading(true);
      setRows([]);
      (async () =>
      {
         /////////////////////////////////////////////////////////////////////////////////////
         // build filter object to submit to backend count & query endpoints                //
         // copy the orderBys & operator into it - but we'll build its criteria one-by-one, //
         // as clones, as we'll need to tweak them a bit                                    //
         /////////////////////////////////////////////////////////////////////////////////////
         const filterForBackend = prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter, pageNumber, rowsPerPage);

         //////////////////////////////////////////
         // figure out joins to use in the query //
         //////////////////////////////////////////
         let queryJoins = null;
         if (tableMetaData?.exposedJoins)
         {
            const visibleJoinTables = getVisibleJoinTables();
            const tablesToAdd = ensureOrderBysFromJoinTablesAreVisibleTables(queryFilter, visibleJoinTables);

            tablesToAdd?.forEach(t => visibleJoinTables.add(t));

            queryJoins = TableUtils.getQueryJoins(tableMetaData, visibleJoinTables);
         }

         //////////////////////////////////////////////////////////////////////////////////////////////////
         // assign a new query id to the query being issued here.  then run both the count & query async //
         // and when they load, store their results associated with this id.                             //
         //////////////////////////////////////////////////////////////////////////////////////////////////
         const thisQueryId = latestQueryId + 1;
         setLatestQueryId(thisQueryId);

         console.log(`Issuing query: ${thisQueryId}`);
         if (tableMetaData.capabilities.has(Capability.TABLE_COUNT))
         {
            if (clearOutCount)
            {
               setTotalRecords(null);
               setDistinctRecords(null);
            }

            let includeDistinct = isJoinMany(tableMetaData, getVisibleJoinTables());
            ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
            // qController.count(tableName, filterForBackend, queryJoins, includeDistinct, tableVariant).then(([count, distinctCount]) => //
            ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
            qControllerV1.count(tableName, apiVersion, filterForBackend, queryJoins, includeDistinct, tableVariant).then(([count, distinctCount]) =>
            {
               console.log(`Received count results for query ${thisQueryId}: ${count} ${distinctCount}`);
               countResults[thisQueryId] = [];
               countResults[thisQueryId].push(count);
               countResults[thisQueryId].push(distinctCount);
               setCountResults(countResults);
               setReceivedCountTimestamp(new Date());
            });
         }

         if (!tableMetaData.capabilities.has(Capability.TABLE_QUERY))
         {
            console.log("Cannot update table - it does not have QUERY capability.");
            return;
         }

         setLastFetchedQFilterJSON(JSON.stringify(queryFilter));
         setLastFetchedVariant(tableVariant);
         ////////////////////////////////////////////////////////////////////////////////////////////////
         // qController.query(tableName, filterForBackend, queryJoins, tableVariant).then((results) => //
         ////////////////////////////////////////////////////////////////////////////////////////////////
         qControllerV1.query(tableName, apiVersion, filterForBackend, queryJoins, tableVariant, getVirtualFieldNamesToInclude()).then((results) =>
         {
            console.log(`Received results for query ${thisQueryId}`);
            queryResults[thisQueryId] = results;
            setQueryResults(queryResults);
            setReceivedQueryTimestamp(new Date());
         })
            .catch((error) =>
            {
               console.log(`Received error for query ${thisQueryId}`);
               console.log(error);

               let errorMessage;
               if (error && error.message)
               {
                  errorMessage = error.message;
               }
               else if (error && error.response && error.response.data && error.response.data.error)
               {
                  errorMessage = error.response.data.error;
               }
               else
               {
                  errorMessage = "Unexpected error running query";
               }

               queryErrors[thisQueryId] = errorMessage;
               setQueryErrors(queryErrors);
               setReceivedQueryErrorTimestamp(new Date());

               throw error;
            });
      })();
   };

   /////////////////////////////////////////////////////////////////////////////////////////////////////////////
   // if, after a column was turned on or off, the set of visibleJoinTables is changed, then update the table //
   // check this on each render - it should only be different if there was a change.  note that putting this  //
   // in handleColumnVisibilityChange "didn't work" - it was always "behind by one" (like, maybe data grid    //
   // calls that function before it updates the visible model or some-such).                                  //
   /////////////////////////////////////////////////////////////////////////////////////////////////////////////
   const newVisibleJoinTables = getVisibleJoinTables();
   let requeriedForVisibleJoins = false;
   if (JSON.stringify([...newVisibleJoinTables.keys()]) != JSON.stringify([...visibleJoinTables.keys()]))
   {
      updateTable("visible joins change");
      setVisibleJoinTables(newVisibleJoinTables);
      requeriedForVisibleJoins = true;
   }

   //////////////////////////////////////////////////////////////////////////////////////////////
   // same idea for columns the backend only computes on request: turning one ON is the one    //
   // column toggle that has to go back to the backend (ordinary column toggles stay local).   //
   // turning one OFF just hides data already on screen, so no query; the count never changes. //
   //////////////////////////////////////////////////////////////////////////////////////////////
   const newVirtualFieldNamesToInclude = getVirtualFieldNamesToInclude();
   if (JSON.stringify(newVirtualFieldNamesToInclude) != JSON.stringify(virtualFieldNamesToInclude))
   {
      const addedNames = newVirtualFieldNamesToInclude.filter(name => virtualFieldNamesToInclude.indexOf(name) == -1);
      if (addedNames.length > 0 && !requeriedForVisibleJoins)
      {
         ///////////////////////////////////////////////////////////////////////////////////////
         // (a join's first visible column changes both lists at once; one query covers both) //
         ///////////////////////////////////////////////////////////////////////////////////////
         updateTable("on-request virtual fields added", false);
      }
      setVirtualFieldNamesToInclude(newVirtualFieldNamesToInclude);
   }

   ///////////////////////////
   // display count results //
   ///////////////////////////
   useEffect(() =>
   {
      if (countResults[latestQueryId] == null || countResults[latestQueryId].length == 0)
      {
         ///////////////////////////////////////////////
         // see same idea in displaying query results //
         ///////////////////////////////////////////////
         console.log(`No count results for id ${latestQueryId}...`);
         return;
      }
      try
      {
         setTotalRecords(countResults[latestQueryId][0]);
         setDistinctRecords(countResults[latestQueryId][1]);
         delete countResults[latestQueryId];
      }
      catch (e)
      {
         console.log(e);
      }
   }, [receivedCountTimestamp]);

   ///////////////////////////
   // display query results //
   ///////////////////////////
   useEffect(() =>
   {
      if (!queryResults[latestQueryId])
      {
         ///////////////////////////////////////////////////////////////////////////////////////////
         // to avoid showing results from an "older" query (e.g., one that was slow, and returned //
         // AFTER a newer one) only ever show results here for the latestQueryId that was issued. //
         ///////////////////////////////////////////////////////////////////////////////////////////
         console.log(`No query results for id ${latestQueryId}...`);
         return;
      }

      console.log(`Outputting results for query ${latestQueryId}...`);
      const results = queryResults[latestQueryId];
      delete queryResults[latestQueryId];
      setLatestQueryResults(results);

      ///////////////////////////////////////////////////////////
      // count how many distinct primary keys are on this page //
      ///////////////////////////////////////////////////////////
      let distinctPrimaryKeySet = new Set<string>();
      for (let i = 0; i < results.length; i++)
      {
         distinctPrimaryKeySet.add(results[i].values.get(tableMetaData.primaryKeyField) as string);
      }
      setDistinctRecordsOnPageCount(distinctPrimaryKeySet.size);

      ////////////////////////////////
      // make the rows for the grid //
      ////////////////////////////////
      const rows = DataGridUtils.makeRows(results, tableMetaData, tableVariant);
      setRows(rows);

      /////////////////////////////////////////////////////////////////////////////////////
      // a fresh result set gets fresh (collapsed) rows; an inline-edit save, which only //
      // swaps one row in place, keeps the user's expanded rows as they were             //
      /////////////////////////////////////////////////////////////////////////////////////
      setExpandedRowIds(new Set());

      setLoading(false);
      setAlertContent(null);
      forceUpdate();
   }, [receivedQueryTimestamp]);

   /////////////////////////
   // display query error //
   /////////////////////////
   useEffect(() =>
   {
      if (!queryErrors[latestQueryId])
      {
         ///////////////////////////////
         // same logic as for success //
         ///////////////////////////////
         console.log(`No query error for id ${latestQueryId}...`);
         return;
      }

      console.log(`Outputting error for query ${latestQueryId}...`);
      const errorMessage = queryErrors[latestQueryId];
      delete queryErrors[latestQueryId];
      setLoading(false);
      setAlertContent(errorMessage);

   }, [receivedQueryErrorTimestamp]);


   /*******************************************************************************
    ** for the recordPicker usage only - clear checkbox-based selection state.
    ** checked rows are keyed page-relative, and the picker submits whatever is in
    ** selectedIds - so checks must not silently ride along across page or filter
    ** changes.  full-query-result / subset selections are left alone - they are
    ** filter-based, not tied to the visible rows.  (the query screen's longstanding
    ** carry-over behavior is intentionally left as-is.)
    *******************************************************************************/
   const clearRecordPickerCheckboxSelection = () =>
   {
      if (usage == "recordPicker" && selectFullFilterState !== "filter" && selectFullFilterState !== "filterSubset")
      {
         setRowSelectionModel([]);
         setSelectedIds([]);
         setSelectFullFilterState("n/a");
      }
   };


   /*******************************************************************************
    ** Event handler from grid - when page number changes
    *******************************************************************************/
   const handlePageNumberChange = (page: number) =>
   {
      if (editingRowId != null)
      {
         setEditingRowId(null);
         setEditingRow(null);
      }

      clearRecordPickerCheckboxSelection();

      setPageNumber(page);
      setLoading(true);
   };

   /*******************************************************************************
    ** Event handler from grid - when rows per page changes
    *******************************************************************************/
   const handleRowsPerPageChange = (size: number) =>
   {
      if (editingRowId != null)
      {
         setEditingRowId(null);
         setEditingRow(null);
      }

      clearRecordPickerCheckboxSelection();

      ////////////////////////////////////////////////////////////////////////////////////
      // reset to the first page - the previous page number is generally out of range    //
      // under the new page size (mui-x v5 used to do this reset for us and fire         //
      // onPageChange(0); v7 does not, which left stale skips querying past the end of   //
      // the table and showing "no rows" until a refresh)                                //
      ////////////////////////////////////////////////////////////////////////////////////
      setPageNumber(0);

      setRowsPerPage(size);
      setLoading(true);

      view.rowsPerPage = size;
      doSetView(view);
   };

   /*******************************************************************************
    ** event handler from grid - when user changes pins
    *******************************************************************************/
   const handlePinnedColumnsChange = (pinnedColumns: GridPinnedColumnFields) =>
   {
      setPinnedColumns(pinnedColumns);
      queryColumns.setPinnedLeftColumns(pinnedColumns.left);
      queryColumns.setPinnedRightColumns(pinnedColumns.right);

      view.queryColumns = queryColumns;
      doSetView(view);
   };

   /*******************************************************************************
    ** event handler from grid - when density changes
    *******************************************************************************/
   const handleDensityChange = (newDensity: GridDensity) =>
   {
      if (newDensity && newDensity !== density)
      {
         setDensity(newDensity);
         localStorageSet(densityLocalStorageKey, JSON.stringify(newDensity));
      }
   };

   /*******************************************************************************
    ** event handler from grid - for when user clicks a row.
    *******************************************************************************/
   const handleRowClick = (params: GridRowParams, event: MuiEvent<React.MouseEvent>, details: GridCallbackDetails) =>
   {
      /////////////////////////////////////////////////////////////////////////
      // while a row is being edited, ignore all row-click navigation events //
      /////////////////////////////////////////////////////////////////////////
      if (editingRowId != null)
      {
         clearTimeout(timerInstance.current.timer);
         return;
      }

      /////////////////////////////////////////////////////////////////
      // if a grid preference window is open, ignore and reset timer //
      /////////////////////////////////////////////////////////////////
      console.log(gridPreferencesWindow);
      if (gridPreferencesWindow !== undefined)
      {
         clearTimeout(timerInstance.current.timer);
         return;
      }

      let id = encodeURIComponent(params.id);
      if (table.primaryKeyField !== "id")
      {
         id = encodeURIComponent(params.row[tableMetaData.primaryKeyField]);
      }
      const tablePath = `${metaData.getTablePathByName(table.name)}/${id}`;
      DataGridUtils.handleRowClick(tablePath, event, gridMouseDownX, gridMouseDownY, navigate, timerInstance);
   };

   /*******************************************************************************
    ** for the record-picker - clicking anywhere in a row changes its selection,
    ** giving a much bigger target than the checkbox/radio itself.  In single-select
    ** the click replaces the selection (radio semantics - it never clears it); in
    ** multi-select it toggles just that row, leaving other picks alone.
    **
    ** Wired through onRowClick rather than by clearing disableSelectionOnClick, so
    ** the query-screen and report-setup usages keep their current behavior.  Rows
    ** carry no record links in picker usage, so nothing competes for this click.
    *******************************************************************************/
   const handlePickerRowClick = (params: GridRowParams, event?: MuiEvent<React.MouseEvent>) =>
   {
      ///////////////////////////////////////////////////////////////////////////////////
      // the grid publishes rowClick for a click anywhere in the row, the checkbox     //
      // cell included, and this runs AFTER the grid has already applied its own       //
      // selection change.  Re-deriving the selection from the stale rowSelectionModel //
      // would then overwrite it - harmless for a plain click, but a shift-click range //
      // gets clobbered down to just the anchor plus the target.  The grid's own       //
      // handling is what we want in that cell, so leave it alone.                     //
      ///////////////////////////////////////////////////////////////////////////////////
      if ((event?.target as HTMLElement)?.closest?.(".MuiDataGrid-cellCheckbox"))
      {
         return;
      }

      if(isSingleSelectPicker)
      {
         handleSelectionChanged([params.id], null);
         return;
      }

      const isAlreadySelected = rowSelectionModel.includes(params.id);
      handleSelectionChanged(isAlreadySelected
         ? rowSelectionModel.filter(id => id != params.id)
         : [...rowSelectionModel, params.id], null);
   };


   /*******************************************************************************
    ** event handler from grid - for when selection (checked rows) changes.
    *******************************************************************************/
   const handleSelectionChanged = (selectionModel: GridRowSelectionModel, details: GridCallbackDetails) =>
   {
      /////////////////////////////////////////////////////////////////////////////////////
      // DataGrid v5 ignores disableMultipleSelection whenever checkboxSelection is on - //
      // see useGridSelection: canHaveMultipleSelection = !disableMultipleSelection ||   //
      // checkboxSelection.  so single-select has to be enforced here: keep only the     //
      // most recently added row, which is the one the user just clicked.                //
      /////////////////////////////////////////////////////////////////////////////////////
      if (isSingleSelectPicker && selectionModel.length > 1)
      {
         const previouslySelected = new Set(rowSelectionModel);
         const justAdded = selectionModel.filter(id => !previouslySelected.has(id));
         selectionModel = justAdded.length > 0 ? [justAdded[justAdded.length - 1]] : [selectionModel[selectionModel.length - 1]];
      }

      ////////////////////////////////////////////////////
      // since we manage this object, we must re-set it //
      ////////////////////////////////////////////////////
      setRowSelectionModel(selectionModel);

      let checkboxesChecked = 0;
      let selectedPrimaryKeys = new Set<string>();
      selectionModel.forEach((value: GridRowId, index: number) =>
      {
         checkboxesChecked++;
         const valueToPush = latestQueryResults[value as number].values.get(tableMetaData.primaryKeyField);
         selectedPrimaryKeys.add(valueToPush as string);
      });
      setSelectedIds([...selectedPrimaryKeys.values()]);

      if (checkboxesChecked === rowsPerPage)
      {
         setSelectFullFilterState("checked");
      }
      else
      {
         setSelectFullFilterState("n/a");
      }
   };

   /*******************************************************************************
    ** event handler from grid - for when the order of columns changes
    *******************************************************************************/
   const handleColumnOrderChange = (columnOrderChangeParams: GridColumnOrderChangeParams) =>
   {
      /////////////////////////////////////////////////////////////////////////////////////
      // get current state from gridApiRef - as the changeParams only have the delta     //
      // and we don't want to worry about being out of sync - just reset fully each time //
      /////////////////////////////////////////////////////////////////////////////////////
      const columnOrdering = gridApiRef.current.state.columns.orderedFields;
      queryColumns.updateColumnOrder(columnOrdering);

      view.queryColumns = queryColumns;
      doSetView(view);
   };


   /*******************************************************************************
    ** event handler from grid - for when user resizes a column
    *******************************************************************************/
   const handleColumnResize = (params: GridColumnResizeParams, event: MuiEvent, details: GridCallbackDetails) =>
   {
      queryColumns.updateColumnWidth(params.colDef.field, params.width);

      view.queryColumns = queryColumns;
      doSetView(view);
   };


   /*******************************************************************************
    ** event handler from grid - for when the sort-model changes (e.g., user clicks
    ** a column header to re-sort table).
    *******************************************************************************/
   const handleSortChange = (gridSort: GridSortModel) =>
   {
      if (editingRowId != null)
      {
         setEditingRowId(null);
         setEditingRow(null);
      }

      clearRecordPickerCheckboxSelection();

      ///////////////////////////////////////
      // store the sort model for the grid //
      ///////////////////////////////////////
      setColumnSortModel([...(gridSort ?? [])]);

      ////////////////////////////////////////////////
      // convert the grid's sort to qqq-filter sort //
      ////////////////////////////////////////////////
      queryFilter.orderBys = [];
      for (let i = 0; i < gridSort?.length; i++)
      {
         const fieldName = gridSort[i].field;
         const isAscending = gridSort[i].sort == "asc";
         queryFilter.orderBys.push(new QFilterOrderBy(fieldName, isAscending));
      }

      ///////////////////////////////////////////////////////////////////////////////
      // set a default order-by, if none is otherwise present.  prefer the primary //
      // key, fall back to first sortable field, or omit if no field is sortable.  //
      ///////////////////////////////////////////////////////////////////////////////
      if (queryFilter.orderBys.length == 0)
      {
         const defaultOrderByField = TableUtils.getDefaultOrderByFieldName(tableMetaData);
         if (defaultOrderByField)
         {
            queryFilter.orderBys.push(new QFilterOrderBy(defaultOrderByField, false));
         }
      }

      ////////////////////////////////
      // store the new query filter //
      ////////////////////////////////
      doSetQueryFilter(queryFilter);
   };


   /*******************************************************************************
    **
    *******************************************************************************/
   const handleColumnHeaderClick = (params: GridColumnHeaderParams, event: MuiEvent, details: GridCallbackDetails): void =>
   {
      event.defaultMuiPrevented = true;
   };


   /*******************************************************************************
    ** bigger than doSetView - this method does call doSetView, but then also
    ** updates all other related state on the screen from the view.
    *******************************************************************************/
   const activateView = (view: RecordQueryView): void =>
   {
      /////////////////////////////////////////////////////////////////////////////////////////////
      // pass the 'isFromActivateView' flag into these functions - so that they don't try to set //
      // the filter (or columns) back into the old view.                                         //
      /////////////////////////////////////////////////////////////////////////////////////////////
      doSetQueryFilter(view.queryFilter, true);
      doSetQueryColumns(view.queryColumns, true);

      setRowsPerPage(view.rowsPerPage ?? defaultRowsPerPage);
      setMode(view.mode ?? defaultMode);
      setQuickFilterFieldNames(view.quickFilterFieldNames ?? []); // todo not i think ?? getDefaultQuickFilterFieldNames(tableMetaData));

      //////////////////////////////////////////////////////////////////////////////////////////////////
      // do this last - in case anything in the view got modified in any of those other doSet methods //
      //////////////////////////////////////////////////////////////////////////////////////////////////
      doSetView(view);

      ///////////////////////////////////////////////////////////////////////////////////////////////////////
      // do this in a timeout - so the current view can get set into state properly, before it potentially //
      // gets modified inside these calls (e.g., if a new field gets turned on)                            //
      //                                                                                                   //
      // @ts-ignore                                                                                        //
      ///////////////////////////////////////////////////////////////////////////////////////////////////////
      setTimeout(() => basicAndAdvancedQueryControlsRef?.current?.ensureAllFilterCriteriaAreActiveQuickFilters(view.queryFilter, "activatedView"));
   };


   /*******************************************************************************
    ** Wrapper around setQueryFilter that also puts it in the view, and calls doSetView
    *******************************************************************************/
   const doSetQueryFilter = (queryFilter: QQueryFilter, isFromActivateView = false): void =>
   {
      if (editingRowId != null)
      {
         setEditingRowId(null);
         setEditingRow(null);
      }

      ////////////////////////////////////////////////////////////////////////////////////////
      // re-assert the picker's locked criteria.  every filter change funnels through here, //
      // so this is the one place that has to enforce them - rather than guarding each of   //
      // the many ways they could otherwise be dropped (the criteria row's remove, the      //
      // quick filter's x, Reset All Changes, choosing a saved view, New View...).          //
      //                                                                                    //
      // any criterion matching a locked one is removed from what was passed in before      //
      // prepending, so this cannot accumulate duplicates.  a criterion the user added on   //
      // the same FIELD is left alone - only matches on all of fieldName/operator/values    //
      // are dropped.                                                                       //
      //                                                                                    //
      // the match is on that normalized shape rather than whole-object JSON because        //
      // CustomFilterPanel assigns an `id` onto live criteria as react keys, mutating the   //
      // view's cloned copy in place.  Whole-object JSON would stop recognizing the clone   //
      // as a duplicate the moment the user opened the advanced filter panel, and the next  //
      // call would prepend a SECOND copy of the locked criterion - leaving it visible      //
      // twice, the second one editable and removable.                                      //
      ////////////////////////////////////////////////////////////////////////////////////////
      if (lockedCriteria.length > 0)
      {
         const normalizeCriteria = (c: QFilterCriteria) => JSON.stringify({fieldName: c?.fieldName, operator: c?.operator, values: c?.values});
         const lockedNormalized = lockedCriteria.map(normalizeCriteria);
         const usersCriteria = (queryFilter.criteria ?? []).filter(c => !lockedNormalized.includes(normalizeCriteria(c)));
         queryFilter.criteria = [...lockedCriteria, ...usersCriteria];

         ////////////////////////////////////////////////////////////////////////////////////////////////////
         // and RECORD - but do not rewrite - an Or that arrives while criteria are locked.                //
         // The And itself is enforced at query time, in prepQueryFilterForBackendEnforcingLockedCriteria. //
         // The panel's caption reads this flag and tells the user their Or is being treated               //
         // as an And for as long as these criteria are locked.                                            //
         //                                                                                                //
         // Re-evaluated on every filter change rather than latched on, since a different                  //
         // saved view can bring an And back in.                                                           //
         ////////////////////////////////////////////////////////////////////////////////////////////////////
         lockedCriteriaIsOverridingBooleanOperator.current = (queryFilter.booleanOperator === "OR");
      }

      console.log(`Setting a new query filter: ${JSON.stringify(queryFilter)}`);

      ///////////////////////////////////////////////////
      // when we have a new filter, go back to page 0. //
      ///////////////////////////////////////////////////
      setPageNumber(0);

      ////////////////////////////////////////////////////////////////////////////////
      // in case there's no orderBys, set default here.  prefer the primary key,    //
      // fall back to first sortable field, or omit entirely if nothing's sortable. //
      ////////////////////////////////////////////////////////////////////////////////
      if (!queryFilter.orderBys || queryFilter.orderBys.length == 0)
      {
         const defaultOrderByField = TableUtils.getDefaultOrderByFieldName(tableMetaData);
         queryFilter.orderBys = defaultOrderByField
            ? [new QFilterOrderBy(defaultOrderByField, false)]
            : [];
         view.queryFilter = queryFilter;
      }

      ////////////////////////////////////////////////////////////////////////////////////////////////
      // in case the order-by is from a join table, and that table doesn't have any visible fields, //
      // then activate the order-by field itself                                                    //
      ////////////////////////////////////////////////////////////////////////////////////////////////
      ensureOrderBysFromJoinTablesAreVisibleTables(queryFilter);

      //////////////////////////////
      // set the filter state var //
      //////////////////////////////
      setQueryFilter(queryFilter);

      ///////////////////////////////////////////////////////
      // propagate filter's orderBy into grid's sort model //
      ///////////////////////////////////////////////////////
      const gridSort = FilterUtils.getGridSortFromQueryFilter(queryFilter);
      setColumnSortModel(gridSort);

      ///////////////////////////////////////////////
      // put this query filter in the current view //
      ///////////////////////////////////////////////
      if (!isFromActivateView)
      {
         view.queryFilter = queryFilter;
         doSetView(view);
      }

      ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      // this force-update causes a re-render that'll see the changed filter hash/json string, and make an updateTable run (if appropriate) //
      ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      forceUpdate();
   };

   /*******************************************************************************
    ** Wrapper around setQueryColumns that also sets column models for the grid, puts
    ** updated queryColumns in the view, and calls doSetView
    *******************************************************************************/
   const doSetQueryColumns = (queryColumns: QQueryColumns, isFromActivateView = false): void =>
   {
      ///////////////////////////////////////////////////////////////////////////////////////
      // if we didn't get queryColumns from our view, it should be a PreLoadQueryColumns - //
      // so that means we should now replace it with defaults for the table.               //
      ///////////////////////////////////////////////////////////////////////////////////////
      if (queryColumns instanceof PreLoadQueryColumns || queryColumns.columns.length == 0)
      {
         console.log(`Building new default QQueryColumns for table [${tableMetaData.name}]`);
         queryColumns = QQueryColumns.buildDefaultForTable(tableMetaData);
         view.queryColumns = queryColumns;
      }

      setQueryColumns(queryColumns);

      ////////////////////////////////
      // set the DataGridPro models //
      ////////////////////////////////
      setupGridColumnModels(metaData, tableMetaData, queryColumns);
      /////////////////////////////////////////////////////////////////////////
      // const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage); //
      /////////////////////////////////////////////////////////////////////////

      ///////////////////////////////////////////
      // put these columns in the current view //
      ///////////////////////////////////////////
      if (!isFromActivateView)
      {
         view.queryColumns = queryColumns;
         doSetView(view);
      }
   };


   /*******************************************************************************
    ** Event handler from BasicAndAdvancedQueryControls for when quickFilterFields change
    ** or other times we need to change them (e.g., activating a view)
    *******************************************************************************/
   const doSetQuickFilterFieldNames = (names: string[]): void =>
   {
      setQuickFilterFieldNames([...(names ?? [])]);

      view.quickFilterFieldNames = names;
      doSetView(view);
   };


   /*******************************************************************************
    ** Wrapper around setMode - places it into the view and state.
    *******************************************************************************/
   const doSetMode = (newValue: string) =>
   {
      setMode(newValue);

      view.mode = newValue;
      doSetView(view);
   };


   /*******************************************************************************
    ** Helper function for launching processes - counts selected records.
    *******************************************************************************/
   const getNoOfSelectedRecords = () =>
   {
      if (selectFullFilterState === "filter")
      {
         if (isJoinMany(tableMetaData, getVisibleJoinTables()))
         {
            return (distinctRecords);
         }
         return (totalRecords);
      }

      return (selectedIds.length);
   };

   /*******************************************************************************
    ** get a query-string to put on the url to indicate what records are going into
    ** a process.
    *******************************************************************************/
   const getRecordsQueryString = (): string =>
   {
      if (selectFullFilterState === "filter")
      {
         const filterForBackend = prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter);
         filterForBackend.skip = 0;
         filterForBackend.limit = null;
         return ProcessUtils.buildFilterJSONQueryString(JSON.stringify(filterForBackend));
      }

      if (selectFullFilterState === "filterSubset")
      {
         const filterForBackend = prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter);
         filterForBackend.skip = 0;
         filterForBackend.limit = selectionSubsetSize;
         return ProcessUtils.buildFilterJSONQueryString(JSON.stringify(filterForBackend));
      }

      if (selectedIds.length > 0)
      {
         return `?recordsParam=recordIds&recordIds=${selectedIds.map(r => encodeURIComponent(r)).join(",")}`;
      }

      return "";
   };


   /*******************************************************************************
    ** get the current selection of records, in the shape processes accept - either
    ** an array of record ids, or a QQueryFilter (prepped for the backend), if the
    ** user selected the full query result (or a subset of it).  an empty selection
    ** comes back as an empty array.
    *******************************************************************************/
   const getSelectionAsRecordIdsOrFilter = (): string[] | QQueryFilter =>
   {
      if (selectFullFilterState === "filter")
      {
         const filterForBackend = prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter);
         filterForBackend.skip = 0;
         filterForBackend.limit = null;
         return (filterForBackend);
      }
      else if (selectFullFilterState === "filterSubset")
      {
         const filterForBackend = prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter);
         filterForBackend.skip = 0;
         filterForBackend.limit = selectionSubsetSize;
         return (filterForBackend);
      }
      else if (selectedIds.length > 0)
      {
         return ([...selectedIds]);
      }

      return ([]);
   };


   /*******************************************************************************
    ** resolve the current selection all the way to record ids (for hosts - e.g.,
    ** the record-picker's association flow - that need ids, not a filter).
    ** checkbox selections return their ids directly; filter-based selections run
    ** a query right here, where the queryJoins & tableVariant context lives (see
    ** updateTable's "figure out joins to use in the query"), so criteria on
    ** exposed-join fields and variant-backed tables resolve correctly.  honors
    ** the passed limit (callers wanting overflow detection pass max+1); returns
    ** distinct pks (join-many queries can repeat them).
    **
    ** Pages until it holds `limit` DISTINCT ids rather than issuing one query for
    ** `limit` rows: the backend limit caps rows, and with a join-many exposed join
    ** one record occupies several of them.  A single query for max+1 rows could
    ** then come back as far fewer than max+1 distinct ids, and a caller using
    ** "more than max came back" as its overflow signal (RecordGridWidget does)
    ** would read a cut-off selection as one that fit, silently dropping records.
    ** That paging sorts by the primary key as a tiebreaker, so it does not re-read
    ** the same rows, and is bounded (see maxPages) - THROWING on the bound rather
    ** than returning short, since a short return is the one thing the caller reads
    ** as "it all fit".
    *******************************************************************************/
   const getSelectionAsRecordIds = async (limit: number): Promise<string[]> =>
   {
      if (selectFullFilterState === "filter" || selectFullFilterState === "filterSubset")
      {
         const filterForBackend = prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter);
         const rowLimit = (selectFullFilterState === "filterSubset" && selectionSubsetSize != null && selectionSubsetSize < limit) ? selectionSubsetSize : limit;

         let queryJoins = null;
         if (tableMetaData?.exposedJoins)
         {
            ////////////////////////////////////////////////////////////////////////////////////
            // the PURE collector here, deliberately - resolving a selection must not change  //
            // what the user is looking at.  The side-effecting variant flips column          //
            // visibility, mutates view.queryColumns and triggers a fresh query, all mid-save //
            ////////////////////////////////////////////////////////////////////////////////////
            const visibleJoinTables = getVisibleJoinTables();
            const tablesToAdd = collectOrderByJoinTablesNotVisible(queryFilter, visibleJoinTables);
            tablesToAdd?.forEach(t => visibleJoinTables.add(t));
            queryJoins = TableUtils.getQueryJoins(tableMetaData, visibleJoinTables);
         }

         /////////////////////////////////////////////////////////////////////////////////////
         // paging with skip/limit is only stable if the sort is deterministic, and the     //
         // user's order-by is frequently a non-unique column (a status, a date) - under    //
         // which the backend may hand back overlapping or skipped rows between pages, so   //
         // the distinct set comes up short of the true first N.  Append the primary key as //
         // a tiebreaker (unless it is already an order-by) to make the order total.        //
         /////////////////////////////////////////////////////////////////////////////////////
         const primaryKeyField = tableMetaData?.primaryKeyField;
         if (primaryKeyField && !(filterForBackend.orderBys ?? []).some(orderBy => orderBy.fieldName == primaryKeyField))
         {
            filterForBackend.orderBys = [...(filterForBackend.orderBys ?? []), new QFilterOrderBy(primaryKeyField, true)];
         }

         const distinctIds = new Set<string>();
         let skip = 0;

         /////////////////////////////////////////////////////////////////////////////////////
         // and a cap on the round-trips.  With a high fan-out join-many (say 50 rows per   //
         // record) a rowLimit of 1001 would otherwise issue ~50 sequential queries with    //
         // nothing bounding it, all while the caller's Save is waiting.                    //
         //                                                                                 //
         // Reaching the cap THROWS rather than returning what it happens to hold.  A short //
         // return is indistinguishable from a result set that ran out, and that is exactly //
         // how the callers read it - "fewer than the max+1 I asked for" means "it all fit" //
         // - so a cut-off selection would be trimmed to whatever the cap reached and the   //
         // user told nothing.  The picker catches this and shows the message, staying open //
         // so the filter can be narrowed.                                                  //
         /////////////////////////////////////////////////////////////////////////////////////
         const maxPages = 10;
         let exhaustedResultSet = false;

         for (let page = 0; page < maxPages && distinctIds.size < rowLimit; page++)
         {
            filterForBackend.skip = skip;
            filterForBackend.limit = rowLimit;

            const results = await qControllerV1.query(tableName, apiVersion, filterForBackend, queryJoins, tableVariant);

            for (let i = 0; i < results.length; i++)
            {
               const value = results[i].values.get(tableMetaData.primaryKeyField);
               if (value != null)
               {
                  distinctIds.add(String(value));
               }
            }

            ///////////////////////////////////////////////////////////////////////////////
            // a short page is the end of the result set - there is nothing left to page //
            // into, however few distinct ids it produced                                //
            ///////////////////////////////////////////////////////////////////////////////
            if (results.length < rowLimit)
            {
               exhaustedResultSet = true;
               break;
            }

            skip += rowLimit;
         }

         /////////////////////////////////////////////////////////////////////////////////////
         // the cap was reached with rows still to read, so what was collected is a cut-off //
         // selection and there is no way to say so in the return value                     //
         /////////////////////////////////////////////////////////////////////////////////////
         if (!exhaustedResultSet && distinctIds.size < rowLimit)
         {
            throw new Error(`This selection covers too many rows of ${tableMetaData?.label ?? tableName} to resolve. Please narrow the filter, or select records individually.`);
         }

         return ([...distinctIds.values()].slice(0, rowLimit));
      }

      return ([...selectedIds].slice(0, limit));
   };


   /*******************************************************************************
    ** launch/open a modal process.  Ends up navigating to the process's path w/
    ** records selected via query string.
    *******************************************************************************/
   const openModalProcess = (process: QProcessMetaData = null) =>
   {
      if (editingRowId != null)
      {
         setEditingRowId(null);
         setEditingRow(null);
      }

      setRecordIdsForProcess(getSelectionAsRecordIdsOrFilter());

      navigate(`${metaData?.getTablePathByName(tableName)}/${process.name}${getRecordsQueryString()}`);
   };


   /*******************************************************************************
    ** close callback for modal processes
    *******************************************************************************/
   const closeModalProcess = (event: object, reason: string) =>
   {
      if (reason === "backdropClick" || reason === "escapeKeyDown")
      {
         return;
      }

      /////////////////////////////////////////////////////////////////////////
      // when closing a modal process, navigate up to the table being viewed //
      /////////////////////////////////////////////////////////////////////////
      const newPath = location.pathname.split("/");
      newPath.pop();
      navigate(newPath.join("/"));

      updateTable("close modal process");
   };


   /*******************************************************************************
    ** function to open one of the bulk (insert/edit/delete) processes.
    *******************************************************************************/
   const openBulkProcess = (processNamePart: "Insert" | "Edit" | "Delete" | "EditWithFile", processLabelPart: "Load" | "Edit" | "Delete" | "Edit With File") =>
   {
      const processList = allTableProcesses.filter(p => p.name.endsWith(`.bulk${processNamePart}`));
      if (processList.length > 0)
      {
         openModalProcess(processList[0]);
      }
      else
      {
         setAlertContent(`Could not find Bulk ${processLabelPart} process for this table.`);
      }
   };

   /*******************************************************************************
    ** Event handler for the bulk-load process being selected
    *******************************************************************************/
   const bulkLoadClicked = () =>
   {
      openBulkProcess("Insert", "Load");
   };


   /*******************************************************************************
    ** Event handler for the bulk-edit process being selected
    *******************************************************************************/
   const bulkEditClicked = () =>
   {
      if (getNoOfSelectedRecords() === 0)
      {
         setAlertContent("No records were selected to Bulk Edit.");
         return;
      }
      openBulkProcess("Edit", "Edit");
   };


   /*******************************************************************************
    ** Event handler for the bulk-edit-with-file process being selected
    *******************************************************************************/
   const bulkEditWithFileClicked = () =>
   {
      openBulkProcess("EditWithFile", "Edit With File");
   };


   /*******************************************************************************
    ** Event handler for the bulk-delete process being selected
    *******************************************************************************/
   const bulkDeleteClicked = () =>
   {
      if (getNoOfSelectedRecords() === 0)
      {
         setAlertContent("No records were selected to Bulk Delete.");
         return;
      }
      openBulkProcess("Delete", "Delete");
   };


   /*******************************************************************************
    ** Event handler for selecting a process from the menu
    *******************************************************************************/
   const processClicked = (process: QProcessMetaData) =>
   {
      if (process.minInputRecords != null && process.minInputRecords > 0 && getNoOfSelectedRecords() === 0)
      {
         setAlertContent(`No records were selected for the process: ${process.label}`);
         return;
      }
      else if (process.minInputRecords != null && getNoOfSelectedRecords() < process.minInputRecords)
      {
         setAlertContent(`Too few records were selected for the process: ${process.label}.  A minimum of ${process.minInputRecords} is required.`);
         return;
      }
      else if (process.maxInputRecords != null && getNoOfSelectedRecords() > process.maxInputRecords)
      {
         setAlertContent(`Too many records were selected for the process: ${process.label}.  A maximum of ${process.maxInputRecords} is allowed.`);
         return;
      }

      ///////////////////////////////////////////////////////////////////////////////////////
      // todo - let the process specify that it needs initial rows - err if none selected. //
      //  alternatively, let a process itself have an initial screen to select rows...     //
      ///////////////////////////////////////////////////////////////////////////////////////
      openModalProcess(process);
   };


   //////////////////////////////////////////////
   // custom pagination component for DataGrid //
   //////////////////////////////////////////////
   function CustomPagination()
   {
      return (<CustomPaginationComponent
         tableMetaData={tableMetaData}
         rows={rows}
         totalRecords={totalRecords}
         distinctRecords={distinctRecords}
         pageNumber={pageNumber}
         rowsPerPage={rowsPerPage}
         loading={loading}
         isJoinMany={isJoinMany(tableMetaData, getVisibleJoinTables())}
         handlePageChange={handlePageNumberChange}
         handleRowsPerPageChange={handleRowsPerPageChange}
      />);
   }

   /////////////////////////////////////////
   // custom loading overlay for DataGrid //
   /////////////////////////////////////////
   function CustomLoadingOverlay()
   {
      return (
         <LinearProgress color="info" />
      );
   }

   /*******************************************************************************
    ** wrapper around setting current saved view (as a QRecord) - which also activates
    ** that view.
    *******************************************************************************/
   const doSetCurrentSavedView = (savedViewRecord: QRecord) =>
   {
      if (savedViewRecord == null)
      {
         console.log("doSetCurrentView called with a null view record - calling doClearCurrentSavedView, and activating tableDefaultView instead.");
         doClearCurrentSavedView();
         activateView(buildTableDefaultView(tableMetaData));
         return;
      }

      setCurrentSavedView(savedViewRecord);

      const viewJson = savedViewRecord.values.get("viewJson");
      const newView = RecordQueryView.buildFromJSON(viewJson);

      activateView(newView);

      ////////////////////////////////////////////////////////////////
      // todo can/should/does this move into the view's "identity"? //
      ////////////////////////////////////////////////////////////////
      localStorageSet(currentSavedViewLocalStorageKey, `${savedViewRecord.values.get("id")}`);
   };


   /*******************************************************************************
    ** wrapper around un-setting current saved view and removing its id from local-stroage
    *******************************************************************************/
   const doClearCurrentSavedView = () =>
   {
      setCurrentSavedView(null);
      localStorageRemove(currentSavedViewLocalStorageKey);
   };


   /*******************************************************************************
    **
    *******************************************************************************/
   const buildTableDefaultView = (tableMetaData: QTableMetaData): RecordQueryView =>
   {
      const newDefaultView = new RecordQueryView();
      const defaultOrderByField = TableUtils.getDefaultOrderByFieldName(tableMetaData);
      const defaultOrderBys = defaultOrderByField ? [new QFilterOrderBy(defaultOrderByField, false)] : [];
      newDefaultView.queryFilter = new QQueryFilter([], defaultOrderBys);
      newDefaultView.queryColumns = QQueryColumns.buildDefaultForTable(tableMetaData);
      newDefaultView.viewIdentity = "empty";
      newDefaultView.rowsPerPage = defaultRowsPerPage;
      newDefaultView.quickFilterFieldNames = [];
      newDefaultView.mode = defaultMode;
      return newDefaultView;
   };

   /*******************************************************************************
    ** event handler for SavedViews component, to handle user selecting a view
    ** (or clearing / selecting new)
    *******************************************************************************/
   const handleSavedViewChange = async (selectedSavedViewId: number) =>
   {
      ////////////////////////////////////////////////////////////////////////////////////////
      // clear warnings if changing views - they should never persist between views, right? //
      // also - this is the callback for the 'reset all changes' link, which should do it.  //
      ////////////////////////////////////////////////////////////////////////////////////////
      setWarningAlertList([]);

      if (selectedSavedViewId != null)
      {
         doRecordAnalytics({category: "tableEvents", action: "activateSavedView", label: tableMetaData.label});

         //////////////////////////////////////////////
         // fetch, then activate the selected filter //
         //////////////////////////////////////////////
         setLoading(true);
         setLoadingSavedView(true);
         const qRecord = await fetchSavedView(selectedSavedViewId);
         setLoading(false);
         setLoadingSavedView(false);
         doSetCurrentSavedView(qRecord);
      }
      else
      {
         /////////////////////////////////
         // this is 'new view' - right? //
         /////////////////////////////////
         doRecordAnalytics({category: "tableEvents", action: "activateNewView", label: tableMetaData.label});

         //////////////////////////////
         // wipe away the saved view //
         //////////////////////////////
         setCurrentSavedView(null);
         localStorageRemove(currentSavedViewLocalStorageKey);

         ///////////////////////////////////////////////
         // activate a new default view for the table //
         ///////////////////////////////////////////////
         activateView(buildTableDefaultView(tableMetaData));
      }
   };

   /*******************************************************************************
    ** utility function to fetch a saved view from the backend.
    *******************************************************************************/
   const fetchSavedView = async (id: number): Promise<QRecord> =>
   {
      let qRecord = null;
      const formData = new FormData();
      formData.append("id", String(id));
      formData.append(QController.STEP_TIMEOUT_MILLIS_PARAM_NAME, String(60 * 1000));
      const processResult = await qController.processInit("querySavedView", formData, qController.defaultMultipartFormDataHeaders());
      if (processResult instanceof QJobError)
      {
         const jobError = processResult as QJobError;
         console.error("Could not retrieve saved view: " + jobError.userFacingError);
         setAlertContent("There was an error loading the selected view.");
      }
      else
      {
         const result = processResult as QJobComplete;
         qRecord = new QRecord(result.values.savedViewList[0]);

         //////////////////////////////////////////////////////////////////////////////
         // make the view json a good and healthy object for the UI here.            //
         // such as, making values be what they'd be in the UI (not necessarily      //
         // what they're like in the backend); similarly, set anything that's unset. //
         //////////////////////////////////////////////////////////////////////////////
         const viewJson = qRecord.values.get("viewJson");
         const newView = RecordQueryView.buildFromJSON(viewJson);

         setWarningAlert(null);
         reconcileCurrentTableMetaDataWithView(newView, "loadingSavedView");

         newView.viewIdentity = "savedView:" + id;

         ///////////////////////////////////////////////////////////////////
         // e.g., translate possible values from ids to objects w/ labels //
         ///////////////////////////////////////////////////////////////////
         await FilterUtils.cleanupValuesInFilerFromQueryString(qController, tableMetaData, newView.queryFilter);

         ///////////////////////////
         // set columns if absent //
         ///////////////////////////
         if (!newView.queryColumns || !newView.queryColumns.columns || newView.queryColumns.columns?.length == 0)
         {
            newView.queryColumns = QQueryColumns.buildDefaultForTable(tableMetaData);
         }

         qRecord.values.set("viewJson", JSON.stringify(newView));
      }

      return (qRecord);
   };


   /*******************************************************************************
    ** after a page-load, or before activating a saved view, make sure that no
    ** fields are missing from its column list, and that no deleted-fields are still
    ** being used.
    *******************************************************************************/
   const reconcileCurrentTableMetaDataWithView = (view: RecordQueryView, useCase: "initialPageLoad" | "loadingSavedView") =>
   {
      let changedView = false;
      const removedFieldNames = new Set<string>();
      const removedFilterReasons = new Set<string>();

      if (view.queryColumns?.columns?.length > 0)
      {
         const fieldNamesInView: { [name: string]: boolean } = {};
         view.queryColumns?.columns?.forEach(column => fieldNamesInView[column.name] = true);
         for (let i = 0; i < tableDefaultView?.queryColumns?.columns.length; i++)
         {
            const currentColumn = tableDefaultView?.queryColumns?.columns[i];
            if (!fieldNamesInView[currentColumn.name])
            {
               console.log(`Adding a new column to this view ${currentColumn.name}`);
               view.queryColumns.addColumnForNewField(tableMetaData, currentColumn.name, useCase == "initialPageLoad");
               changedView = true;
            }
            else
            {
               delete fieldNamesInView[currentColumn.name];
            }
         }

         //////////////////////////////////////////////////////////////
         // delete, from the view, any fields no longer in the table //
         // or that are no longer selectable on this table.          //
         //////////////////////////////////////////////////////////////
         const visibilityToggleStates = view.queryColumns.getVisibilityToggleStates();
         for (let fieldName in fieldNamesInView)
         {
            console.log(`Deleting an old column from this view ${fieldName}`);

            if (visibilityToggleStates[fieldName])
            {
               /////////////////////////////////////////////////////////////////////////////////////////////
               // all available columns in the table (and its joins) are in the view queryColumns object, //
               // but we only want/need to tell a user if a visible one got removed.                      //
               /////////////////////////////////////////////////////////////////////////////////////////////
               removedFieldNames.add(fieldName);
            }

            view.queryColumns.deleteColumnForOldField(tableMetaData, fieldName);
            changedView = true;
         }

         //////////////////////////////////////////////////////////////////////////////////////////
         // also drop columns whose field still exists on the table, but lacks FIELD_SELECTABLE. //
         //////////////////////////////////////////////////////////////////////////////////////////
         for (let i = 0; i < view.queryColumns.columns.length; i++)
         {
            const col = view.queryColumns.columns[i];
            const [field] = TableUtils.getFieldAndTable(tableMetaData, col.name);
            if (field != null && field.capabilities?.size && !field.capabilities.has(Capability.FIELD_SELECTABLE))
            {
               console.log(`Deleting a no-longer-selectable column from this view ${col.name}`);
               if (visibilityToggleStates[col.name])
               {
                  removedFilterReasons.add((field.label ?? col.name) + " is not selectable on this table.");
               }
               view.queryColumns.deleteColumnForOldField(tableMetaData, col.name);
               changedView = true;
               i--;
            }
         }
      }

      //////////////////////////////////////////////////////////////////////////
      // look for deleted fields, or fields lacking FIELD_FILTER_CRITERIA, as //
      // criteria - recursing into subFilters as well.                        //
      //////////////////////////////////////////////////////////////////////////
      const reconcileCriteriaIn = (filter: QQueryFilter) =>
      {
         for (let i = 0; i < filter?.criteria?.length; i++)
         {
            const fieldName = filter.criteria[i].fieldName;
            const [field, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, fieldName);
            if (field == null)
            {
               console.log(`Deleting an old criteria field from this view ${fieldName}`);
               filter.criteria.splice(i, 1);
               changedView = true;
               removedFieldNames.add(fieldName);
               i--;
            }
            else if (field.capabilities?.size && !field.capabilities.has(Capability.FIELD_FILTER_CRITERIA))
            {
               console.log(`Deleting a no-longer-filterable criteria field from this view ${fieldName}`);
               filter.criteria.splice(i, 1);
               changedView = true;
               removedFilterReasons.add((field.label ?? fieldName) + " is not filterable on this table.");
               i--;
            }
            else if (filter.criteria[i].operator && !field.isCriteriaOperatorAllowed(filter.criteria[i].operator))
            {
               const operator = filter.criteria[i].operator;
               console.log(`Removing criteria for ${fieldName} — operator ${operator} is not allowed`);
               filter.criteria.splice(i, 1);
               changedView = true;
               removedFilterReasons.add(operator + " is not allowed for " + (field.label ?? fieldName) + ".");
               i--;
            }
         }

         if (filter?.subFilters?.length > 0)
         {
            for (let s = 0; s < filter.subFilters.length; s++)
            {
               reconcileCriteriaIn(filter.subFilters[s]);
            }
         }
      };
      reconcileCriteriaIn(view?.queryFilter);

      ////////////////////////////////////////////////////////////////////////////
      // look for deleted fields, or fields lacking FIELD_ORDER_BY, as orderBys //
      ////////////////////////////////////////////////////////////////////////////
      for (let i = 0; i < view?.queryFilter?.orderBys?.length; i++)
      {
         const fieldName = view.queryFilter.orderBys[i].fieldName;
         const [field, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, fieldName);
         if (field == null)
         {
            console.log(`Deleting an old orderBy field from this view ${fieldName}`);
            view.queryFilter.orderBys.splice(i, 1);
            changedView = true;
            removedFieldNames.add(fieldName);
            i--;
         }
         else if (!field.capabilities?.has(Capability.FIELD_ORDER_BY))
         {
            console.log(`Deleting a no-longer-sortable orderBy field from this view ${fieldName}`);
            view.queryFilter.orderBys.splice(i, 1);
            changedView = true;
            removedFilterReasons.add((field.label ?? fieldName) + " is not sortable on this table.");
            i--;
         }
      }

      ////////////////////////////////////////////////////////////////////////////////////////
      // look for deleted fields, or fields lacking FIELD_FILTER_CRITERIA, as quick-filters //
      ////////////////////////////////////////////////////////////////////////////////////////
      for (let i = 0; i < view?.quickFilterFieldNames?.length; i++)
      {
         const fieldName = view.quickFilterFieldNames[i];
         const [field, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, fieldName);
         if (field == null)
         {
            console.log(`Deleting an old quikc-filter field from this view ${fieldName}`);
            view.quickFilterFieldNames.splice(i, 1);
            changedView = true;
            removedFieldNames.add(fieldName);
            i--;
         }
         else if (!field.capabilities?.has(Capability.FIELD_FILTER_CRITERIA))
         {
            console.log(`Deleting a no-longer-filterable quick-filter field from this view ${fieldName}`);
            view.quickFilterFieldNames.splice(i, 1);
            changedView = true;
            removedFilterReasons.add((field.label ?? fieldName) + " is not filterable on this table.");
            i--;
         }
      }

      for (let i = 0; i < view?.queryFilter?.criteria?.length; i++)
      {
         const fieldName = view.queryFilter.criteria[i].fieldName;
         const operator = view.queryFilter.criteria[i].operator;
         const [field, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, fieldName);

         /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
         // todo - this could/should be a richer thing, but, this gets past an immediate known case/bug, so going w/ it //
         /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
         if (field?.type == QFieldType.BOOLEAN && operator)
         {
            if ([QCriteriaOperator.EQUALS, QCriteriaOperator.IS_BLANK, QCriteriaOperator.IS_NOT_BLANK].indexOf(operator) == -1)
            {
               console.log(`Deleting a criteria field w/ operator not supported for frontend: ${fieldName}`);
               view.queryFilter.criteria.splice(i, 1);
               changedView = true;
               removedFilterReasons.add(field.label + " has an unsupported operator: " + operator);
               i--;
            }
         }
      }

      if (changedView && useCase == "initialPageLoad")
      {
         activateView(view);
      }

      const removedFieldCount = removedFieldNames.size;
      if (removedFieldCount > 0)
      {
         const plural = removedFieldCount > 1;
         pushWarningAlert(`${removedFieldCount} field${plural ? "s" : ""} that ${plural ? "were" : "was"} part of this view ${plural ? "are" : "is"} no longer in this table, and ${plural ? "were" : "was"} removed from this view (${[...removedFieldNames.values()].join(", ")}).`);
      }

      const removedFilterCount = removedFilterReasons.size;
      if (removedFilterCount > 0)
      {
         const plural = removedFilterCount > 1;
         pushWarningAlert(`${removedFilterCount} filter${plural ? "s" : ""} is misconfigured for this screen and was removed from this view: (Details: ${[...removedFilterReasons.values()].join(", ")}).`);
      }
   };


   /*******************************************************************************
    ** event handler for selecting 'filter' action from columns menu in advanced mode.
    *******************************************************************************/
   const handleColumnMenuAdvancedFilterSelection = (fieldName: string) =>
   {
      const newCriteria = new QFilterCriteria(fieldName, null, []);

      if (!queryFilter.criteria)
      {
         queryFilter.criteria = [];
      }

      const length = queryFilter.criteria.length;
      if (length > 0 && !queryFilter.criteria[length - 1].fieldName)
      {
         /////////////////////////////////////////////////////////////////////////////////
         // if the last criteria in the filter has no field name (e.g., a default state //
         // when there's 1 criteria that's all blank - may happen other times too?),    //
         // then replace that criteria with a new one for this field.                   //
         /////////////////////////////////////////////////////////////////////////////////
         queryFilter.criteria[length - 1] = newCriteria;
      }
      else
      {
         //////////////////////////////////////////////////////////////////////
         // else, add a new criteria for this field onto the end of the list //
         //////////////////////////////////////////////////////////////////////
         queryFilter.criteria.push(newCriteria);
      }

      ///////////////////////////
      // open the filter panel //
      ///////////////////////////
      gridApiRef.current.showPreferences(GridPreferencePanelsValue.filters);
   };


   /*******************************************************************************
    * event handler from columns menu - that copies values from that column (for
    * current page)
    *******************************************************************************/
   const copyPageColumnValues = async (column: GridColDef) =>
   {
      let data = "";
      let counter = 0;
      if (latestQueryResults && latestQueryResults.length)
      {
         let [qFieldMetaData, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, column.field);
         for (let i = 0; i < latestQueryResults.length; i++)
         {
            let record = latestQueryResults[i] as QRecord;
            let value = ValueUtils.getUnadornedValueForDisplay(qFieldMetaData, record.values.get(column.field), record.displayValues.get(column.field));

            const isBlank = (value === null || value === undefined);
            if (isBlank)
            {
               value = "";
            }

            data += value + "\n";
            counter++;
         }

         if (counter > 0)
         {
            await navigator.clipboard.writeText(data);
            setSuccessAlert(`Copied ${counter} ${qFieldMetaData.label} value${counter == 1 ? "" : "s"}.`);
            setTimeout(() => setSuccessAlert(null), 5000);
         }
         else
         {
            setWarningAlert(`There are no ${qFieldMetaData.label} values to copy.`);
            setTimeout(() => setWarningAlert(null), 5000);
         }
      }
   };


   /*******************************************************************************
    ** event handler from columns menu - that copies values from that column for the
    ** full query result (e.g., from the backend) - not just the current page.
    *******************************************************************************/
   const copyFullQueryColumnValues = async (column: GridColDef) =>
   {
      const format = "TSV";

      const materialDashboardInstanceMetaData = metaData.supplementalInstanceMetaData?.get("materialDashboard");
      const limit = materialDashboardInstanceMetaData?.queryScreenCopyFullQueryColumnValuesLimit ?? 100000;

      if (totalRecords > limit)
      {
         setErrorAlert(`The current query contains too many rows to be copied (limit: ${ValueUtils.safeToLocaleString(limit)}).`);
         setTimeout(() => setErrorAlert(null), 5000);
         return;
      }

      let [qFieldMetaData, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, column.field);

      const postBody = new FormData();
      postBody.append("format", format);
      postBody.append("limit", limit);
      postBody.append("omitHeaderRow", String(true));
      postBody.append("fields", column.field);

      const filterForBackend = prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter);
      filterForBackend.skip = 0;
      filterForBackend.limit = null;
      postBody.append("filter", JSON.stringify(filterForBackend));

      setInfoAlert(`Copying ${qFieldMetaData.label} values...`);

      qController.axiosRequest(
         {
            method: "post",
            url: `/data/${encodeURIComponent(tableMetaData.name)}/export?format=${format}&limit=${limit}`,
            data: postBody,
            headers: qController.defaultMultipartFormDataHeaders()
         })
         .then(response =>
         {
            (async () =>
            {
               const numberOfValues = response.split("\n").length - 1;
               if (numberOfValues == 0)
               {
                  setInfoAlert(null);
                  setWarningAlert(`There are no ${qFieldMetaData.label} values to copy`);
                  setTimeout(() => setWarningAlert(null), 5000);
                  return;
               }

               try
               {
                  await navigator.clipboard.writeText(response);
                  setInfoAlert(null);

                  const valuesLabel = `${qFieldMetaData.label} value${numberOfValues == 1 ? "" : "s"}`;
                  if (numberOfValues >= limit)
                  {
                     ///////////////////////////////////////////////////////////////////////////////////////
                     // the backend applies `limit` as a hard query limit (ExportAction sets it on the    //
                     // query filter), so an over-sized result gets silently truncated at that many rows. //
                     // we can't distinguish "exactly limit rows matched" from "more than limit matched"  //
                     // without asking for one extra row, so word this as a possibility, not a fact.      //
                     ///////////////////////////////////////////////////////////////////////////////////////
                     setWarningAlert(`Copied ${ValueUtils.safeToLocaleString(numberOfValues)} ${valuesLabel} - the maximum that can be copied at once.  If more rows matched this query, their values were not copied.`);
                     setTimeout(() => setWarningAlert(null), 10000);
                  }
                  else
                  {
                     setSuccessAlert(`Copied ${ValueUtils.safeToLocaleString(numberOfValues)} ${valuesLabel}.`);
                     setTimeout(() => setSuccessAlert(null), 5000);
                  }
               }
               catch (e)
               {
                  setInfoAlert(null);
                  let message = `${e}`;

                  if (!document.hasFocus())
                  {
                     message = "Keep this window active until the copy is complete.  Details: " + message;
                  }

                  setWarningAlert(`Error copying values: ${message}`);
                  setTimeout(() => setWarningAlert(null), 5000);
               }
            })();
         })
         .catch(e =>
         {
            console.error(e);
            setInfoAlert(null);
            setWarningAlert(`Error copying values: ${e?.message ?? e}`);
            setTimeout(() => setWarningAlert(null), 5000);
         });
   };


   /*******************************************************************************
    ** event handler from columns menu - to open the column statistics modal
    *******************************************************************************/
   const openColumnStatistics = async (column: GridColDef) =>
   {
      setFilterForColumnStats(prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter));
      setColumnStatsFieldName(column.field);

      const [field, fieldTable] = TableUtils.getFieldAndTable(tableMetaData, column.field);
      setColumnStatsField(field);
      setColumnStatsFieldTableName(fieldTable.name);
   };


   /*******************************************************************************
    ** close handler for column stats modal
    *******************************************************************************/
   const closeColumnStats = (event: object, reason: string) =>
   {
      if (reason === "backdropClick" || reason === "escapeKeyDown")
      {
         return;
      }

      setColumnStatsFieldName(null);
      setColumnStatsFieldTableName(null);
      setColumnStatsField(null);
   };


   /////////////////////////////////////////////////
   // custom component for the grid's column-menu //
   // todo - break out into own component/file??  //
   /////////////////////////////////////////////////
   const CustomColumnMenu = forwardRef<HTMLUListElement, GridColumnMenuProps>(
      function GridColumnMenu(props: GridColumnMenuProps, ref)
      {
         const {hideMenu, colDef} = props;

         /////////////////////////////////////////////////////////////////////////////
         // see below where this could be used for future additional copy functions //
         // const [copyMoreMenu, setCopyMoreMenu] = useState(null)                  //
         // const openCopyMoreMenu = (event: any) =>                                //
         // {                                                                       //
         // setCopyMoreMenu(event.currentTarget);                                   //
         // event.stopPropagation();                                                //
         // }                                                                       //
         // const closeCopyMoreMenu = () => setCopyMoreMenu(null);                  //
         /////////////////////////////////////////////////////////////////////////////

         const [field] = TableUtils.getFieldAndTable(tableMetaData, colDef.field);

         const canSort        = field?.capabilities?.has(Capability.FIELD_ORDER_BY) ?? true;
         const canFilter      = field?.capabilities?.has(Capability.FIELD_FILTER_CRITERIA) ?? true;
         const canColumnStats = field?.capabilities?.has(Capability.FIELD_GROUP_BY) ?? true;

         ///////////////////////////////////////////////////////////////////////////////////
         // totalRecords is only populated for tables with the COUNT capability, so it    //
         // can't gate the copy options - on a count-less table it stays null forever and //
         // these items would never enable.  Gate page-values on the rows we actually     //
         // have loaded, and full-query values on either signal.                          //
         ///////////////////////////////////////////////////////////////////////////////////
         const hasPageValuesToCopy      = latestQueryResults?.length > 0;
         const hasFullQueryValuesToCopy = hasPageValuesToCopy || totalRecords > 0;

         return (
            ////////////////////////////////////////////////////////////////////////////////////
            // note - the built-in items (sort/hide/pin) render ListItemText at the theme's   //
            // body font - a .MuiDataGrid-menu rule in qqq-override-styles.css pulls them     //
            // down to the 14px that plain MenuItems get, so all options read as one menu     //
            ////////////////////////////////////////////////////////////////////////////////////
            <GridColumnMenuContainer ref={ref} hideMenu={hideMenu} colDef={colDef} open={props.open}>
               {
                  canSort && <GridColumnMenuSortItem onClick={hideMenu} colDef={colDef!} />
               }

               {
                  canFilter && <MenuItem onClick={(e) =>
                  {
                     hideMenu(e);
                     if (mode == "advanced")
                     {
                        handleColumnMenuAdvancedFilterSelection(colDef.field);
                     }
                     else
                     {
                        // @ts-ignore
                        basicAndAdvancedQueryControlsRef.current.addField(colDef.field);
                     }
                  }}>
                     <ListItemIcon><Icon fontSize="small">filter_alt</Icon></ListItemIcon>
                     Filter
                  </MenuItem>
               }

               <GridColumnMenuHideItem onClick={hideMenu} colDef={colDef!} />

               <Divider />
               <GridColumnMenuPinningItem onClick={hideMenu} colDef={colDef!} />
               <Divider />

               <Tooltip title={!hasPageValuesToCopy ? "There are no rows to copy from" : null} placement="right">
                  <span>
                     <MenuItem disabled={!hasPageValuesToCopy} onClick={(e) =>
                     {
                        hideMenu(e);
                        copyPageColumnValues(colDef);
                     }}>
                        <ListItemIcon><Icon fontSize="small">content_copy</Icon></ListItemIcon>
                        Copy page values
                     </MenuItem>
                  </span>
               </Tooltip>

               <Tooltip title={!hasFullQueryValuesToCopy ? "There are no rows to copy from" : null} placement="right">
                  <span>
                     <MenuItem disabled={!hasFullQueryValuesToCopy} onClick={(e) =>
                     {
                        hideMenu(e);
                        copyFullQueryColumnValues(colDef);
                     }}>
                        <ListItemIcon><Icon fontSize="small">copy_all</Icon></ListItemIcon>
                        Copy full query values
                     </MenuItem>
                  </span>
               </Tooltip>

               {/* todo - alternative to the two copy options above, and advanced copy mode
               <MenuItem onClick={(e) =>
               {
                  hideMenu(e);
                  setCopyingFieldName(colDef.field);
               }}>
                  Copy values...
               </MenuItem>
               */}

               {
                  canColumnStats && <MenuItem onClick={(e) =>
                  {
                     hideMenu(e);
                     openColumnStatistics(colDef);
                  }}>
                     <ListItemIcon><Icon fontSize="small">query_stats</Icon></ListItemIcon>
                     Column statistics
                  </MenuItem>
               }

            </GridColumnMenuContainer>
         );
      });

   /////////////////////////////////////////////////////////////
   // custom component for the column header cells            //
   // where we need custom event handlers for the filter icon //
   // todo - break out into own component/file??              //
   /////////////////////////////////////////////////////////////
   const CustomColumnHeaderFilterIconButton = forwardRef<any, ColumnHeaderFilterIconButtonProps>(
      function ColumnHeaderFilterIconButton(props: ColumnHeaderFilterIconButtonProps, ref)
      {
         let showFilter = false;
         for (let i = 0; i < queryFilter?.criteria?.length; i++)
         {
            const criteria = queryFilter.criteria[i];
            if (criteria.fieldName == props.field && validateCriteria(criteria, null).criteriaIsValid)
            {
               showFilter = true;
            }
         }

         if (showFilter)
         {
            return (<IconButton size="small" sx={{p: "2px"}} onClick={(event) =>
            {
               if (mode == "basic")
               {
                  ///////////////////
                  // @ts-ignore !? //
                  ///////////////////
                  basicAndAdvancedQueryControlsRef.current.addField(props.field);
               }
               else
               {
                  gridApiRef.current.showPreferences(GridPreferencePanelsValue.filters);
               }

               event.stopPropagation();
            }}><Icon fontSize="small">filter_alt</Icon></IconButton>);
         }

         return (<></>);
      });

   ////////////////////////////////////////////////
   // custom component for the grid toolbar      //
   // todo - break out into own component/file?? //
   ////////////////////////////////////////////////
   function CustomToolbar()
   {

      /*******************************************************************************
       ** event handler for mouse-down event - helps w/ avoiding accidental clicks into rows
       *******************************************************************************/
      const handleMouseDown: GridEventListener<"cellMouseDown"> = (
         params, // GridRowParams
         event, // MuiEvent<React.MouseEvent<HTMLElement>>
         details, // GridCallbackDetails
      ) =>
      {
         setGridMouseDownX(event.clientX);
         setGridMouseDownY(event.clientY);
         clearTimeout(timerInstance.current.timer);
      };

      /*******************************************************************************
       ** event handler for double-click event - helps w/ avoiding accidental clicks into rows
       *******************************************************************************/
      const handleDoubleClick: GridEventListener<"rowDoubleClick"> = (event: any) =>
      {
         clearTimeout(timerInstance.current.timer);
      };

      const apiRef = useGridApiContext();
      useGridApiEventHandler(apiRef, "cellMouseDown", handleMouseDown);
      useGridApiEventHandler(apiRef, "rowDoubleClick", handleDoubleClick);

      ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      // keep track of any preference windows that are opened in the toolbar, to allow ignoring clicks away from the window //
      ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      useEffect(() =>
      {
         const preferencePanelState = gridPreferencePanelStateSelector(apiRef.current.state);
         setGridPreferencesWindow(preferencePanelState.openedPanelValue);
      });

      const joinIsMany = isJoinMany(tableMetaData, visibleJoinTables);

      const selectionMenuOptions: string[] = [];
      selectionMenuOptions.push(`This page (${ValueUtils.safeToLocaleString(distinctRecordsOnPageCount)} ${joinIsMany ? "distinct " : ""}record${distinctRecordsOnPageCount == 1 ? "" : "s"})`);
      selectionMenuOptions.push(`Full query result (${joinIsMany ? ValueUtils.safeToLocaleString(distinctRecords) + ` distinct record${distinctRecords == 1 ? "" : "s"}` : ValueUtils.safeToLocaleString(totalRecords) + ` record${totalRecords == 1 ? "" : "s"}`})`);
      selectionMenuOptions.push(`Subset of the query result ${selectionSubsetSize ? `(${ValueUtils.safeToLocaleString(selectionSubsetSize)} ${joinIsMany ? "distinct " : ""}record${selectionSubsetSize == 1 ? "" : "s"})` : "..."}`);
      selectionMenuOptions.push("Clear selection");


      /*******************************************************************************
       ** util function to check boxes for some or all rows in the grid, in response to
       ** selection menu actions
       *******************************************************************************/
      function programmaticallySelectSomeOrAllRows(max?: number)
      {
         ///////////////////////////////////////////////////////////////////////////////////////////
         // any time the user selects one of the options from our selection menu,                 //
         // we want to check all the boxes on the screen - and - "select" all of the primary keys //
         // unless they did the subset option - then we'll only go up to a 'max' number           //
         ///////////////////////////////////////////////////////////////////////////////////////////
         const rowSelectionModel: GridRowId[] = [];
         let selectedPrimaryKeys = new Set<string>();
         rows.forEach((value: GridRowModel, index: number) =>
         {
            const primaryKeyValue = latestQueryResults[index].values.get(tableMetaData.primaryKeyField);
            if (max)
            {
               if (selectedPrimaryKeys.size < max)
               {
                  if (!selectedPrimaryKeys.has(primaryKeyValue))
                  {
                     rowSelectionModel.push(value.__rowIndex);
                     selectedPrimaryKeys.add(primaryKeyValue as string);
                  }
               }
            }
            else
            {
               rowSelectionModel.push(value.__rowIndex);
               selectedPrimaryKeys.add(primaryKeyValue as string);
            }
         });
         setRowSelectionModel(rowSelectionModel);
         setSelectedIds([...selectedPrimaryKeys.values()]);
      }


      /*******************************************************************************
       ** event handler (callback) for optiosn in the selection menu
       *******************************************************************************/
      const selectionMenuCallback = (selectedIndex: number) =>
      {
         if (selectedIndex == 0)
         {
            ///////////////
            // this page //
            ///////////////
            programmaticallySelectSomeOrAllRows();
            setSelectFullFilterState("checked");
         }
         else if (selectedIndex == 1)
         {
            ///////////////////////
            // full query result //
            ///////////////////////
            programmaticallySelectSomeOrAllRows();
            setSelectFullFilterState("filter");
         }
         else if (selectedIndex == 2)
         {
            ////////////////////////////
            // subset of query result //
            ////////////////////////////
            setSelectionSubsetSizePromptOpen(true);
         }
         else if (selectedIndex == 3)
         {
            /////////////////////
            // clear selection //
            /////////////////////
            setSelectFullFilterState("n/a");
            setRowSelectionModel([]);
            setSelectedIds([]);
         }
      };

      return (
         <GridToolbarContainer>
            <div>
               <Tooltip title="Refresh Query">
                  <Button id="refresh-button" onClick={() => updateTable("refresh button")} startIcon={<Icon>refresh</Icon>} sx={{pl: "1rem", pr: "0.5rem", minWidth: "unset"}}></Button>
               </Tooltip>
            </div>
            {
               !isPreview && (
                  <div style={{position: "relative"}}>
                     {/* @ts-ignore */}
                     <GridToolbarDensitySelector nonce={undefined} />
                  </div>
               )
            }
            {
               isPreview && (
                  <Tooltip title="Open In New Window">
                     <Button id="open-filter-in-new-window-button" onClick={() => openFilterInNewWindow()} startIcon={<Icon>launch</Icon>} sx={{pl: "1rem", pr: "0.5rem", minWidth: "unset"}}></Button>
                  </Tooltip>
               )
            }

            {
               ///////////////////////////////////////////////////////////////////////////////
               // select-all and subset selection are multi-select concepts - with only one //
               // row selectable they have no meaning, so the whole menu is hidden.         //
               ///////////////////////////////////////////////////////////////////////////////
               (usage == "queryScreen" || (usage == "recordPicker" && !isSingleSelectPicker)) &&
               <div style={{zIndex: 10}}>
                  <MenuButton label="Selection" iconName={selectedIds.length == 0 ? "check_box_outline_blank" : "check_box"} disabled={totalRecords == 0} options={selectionMenuOptions} callback={selectionMenuCallback} />
                  <SelectionSubsetDialog isOpen={selectionSubsetSizePromptOpen} initialValue={selectionSubsetSize} closeHandler={(value) =>
                  {
                     setSelectionSubsetSizePromptOpen(false);

                     if (value !== undefined)
                     {
                        if (typeof value === "number" && value > 0)
                        {
                           programmaticallySelectSomeOrAllRows(value);
                           setSelectionSubsetSize(value);
                           setSelectFullFilterState("filterSubset");
                        }
                        else
                        {
                           setAlertContent("Unexpected value: " + value);
                        }
                     }
                  }} />
               </div>
            }

            <div>
               {
                  selectFullFilterState === "checked" && (
                     <div className="selectionTool">
                        The
                        <strong>{` ${selectedIds.length.toLocaleString()} `}</strong>
                        {joinIsMany ? " distinct " : ""}
                        record{selectedIds.length == 1 ? "" : "s"} on this page {selectedIds.length == 1 ? "is" : "are"} selected.
                     </div>
                  )
               }
               {
                  selectFullFilterState === "filter" && (
                     <div className="selectionTool">
                        {
                           (joinIsMany
                              ? (
                                 distinctRecords == 1
                                    ? (<>The <strong>only 1</strong> distinct record matching this query is selected.</>)
                                    : (<>All <strong>{(distinctRecords ? distinctRecords.toLocaleString() : "")}</strong> distinct records matching this query are selected.</>)
                              )
                              : (<>All <strong>{totalRecords ? totalRecords.toLocaleString() : ""}</strong> records matching this query are selected.</>)
                           )
                        }
                     </div>
                  )
               }
               {
                  selectFullFilterState === "filterSubset" && (
                     <div className="selectionTool">
                        The <a onClick={() => setSelectionSubsetSizePromptOpen(true)} style={{cursor: "pointer"}}><strong>first {ValueUtils.safeToLocaleString(selectionSubsetSize)}</strong></a> {joinIsMany ? "distinct" : ""} record{selectionSubsetSize == 1 ? "" : "s"} matching this query {selectionSubsetSize == 1 ? "is" : "are"} selected.
                     </div>
                  )
               }
               {
                  (selectFullFilterState === "n/a" && selectedIds.length > 0) && (
                     <div className="selectionTool">
                        <strong>{ValueUtils.safeToLocaleString(selectedIds.length)}</strong> {joinIsMany ? "distinct" : ""} {selectedIds.length == 1 ? "record is" : "records are"} selected.
                     </div>
                  )
               }
            </div>
            <div className="pagination">
               <CustomPagination />
            </div>
         </GridToolbarContainer>
      );
   }

   ///////////////////////////////////////////////////////////////////////////////////////////
   // for changes in table controls that don't change the count, call to update the table - //
   // but without clearing out totalRecords (so pagination doesn't flash)                   //
   ///////////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (latestQueryId > 0)
      {
         ////////////////////////////////////////////////////////////////////////////////////////
         // to avoid both this useEffect and the one below from both doing an "initial query", //
         // only run this one if at least 1 query has already been ran                         //
         ////////////////////////////////////////////////////////////////////////////////////////
         updateTable("useEffect(pageNumber,rowsPerPage)", false);
      }
   }, [pageNumber, rowsPerPage]);

   ////////////////////////////////////////////////////////////
   // scroll to the origin when pageNo or rowsPerPage change //
   ////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      document.documentElement.scrollTop = 0;
      document.scrollingElement.scrollTop = 0;
   }, [pageNumber, rowsPerPage]);

   ////////////////////////////////////////////////////////////////////
   // if user doesn't have read permission, just show an error alert //
   ////////////////////////////////////////////////////////////////////
   if (tableMetaData && !tableMetaData.readPermission)
   {
      return (
         <Alert severity="error">
            You do not have permission to view {tableMetaData?.label} records
         </Alert>
      );
   }


   if (pageState == "ready")
   {
      const filterForBackend = prepQueryFilterForBackendEnforcingLockedCriteria(queryFilter);

      ///////////////////////////////////////////////////////////////////////
      // remove the skip & limit (e.g., pagination) from this hash -       //
      // as we have a specific useEffect watching these, specifically      //
      // so we can pass the dont-clear-count flag into updateTable,        //
      // to try to keep the count from flashing back & forth to "Counting" //
      ///////////////////////////////////////////////////////////////////////
      filterForBackend.skip = null;
      filterForBackend.limit = null;

      const newFilterHash = JSON.stringify(filterForBackend);
      if (filterHash != newFilterHash)
      {
         //////////////////////////////////////////////////////////////////////////////////////////
         // this hash excludes skip & limit, so a change here means the filter itself changed    //
         // (not just pagination) - the central spot to drop stale checkbox picks in the picker, //
         // so records checked and then filtered out of view can't silently get submitted.       //
         //////////////////////////////////////////////////////////////////////////////////////////
         clearRecordPickerCheckboxSelection();

         setFilterHash(newFilterHash);
         updateTable("hash change");
      }
   }

   ////////////////////////////////////////////////////////////
   // handle the initial page state -- by fetching meta-data //
   ////////////////////////////////////////////////////////////
   if (pageState == "initial")
   {
      console.log("page state is initial - going to loadingMetaData...");
      setPageState("loadingMetaData");
      pageLoadingState.setLoading();

      (async () =>
      {
         const metaData = await qController.loadMetaData();
         setMetaData(metaData);

         try
         {
            ///////////////////////////////////////////////////////////////////////////
            // const tableMetaData = await qController.loadTableMetaData(tableName); //
            ///////////////////////////////////////////////////////////////////////////
            const tableMetaData = await qControllerV1.loadTableMetaData(tableName, apiVersion);
            setTableMetaData(tableMetaData);
            setTableLabel(tableMetaData.label);

            doRecordAnalytics({location: window.location, title: "Query: " + tableMetaData.label});

            const processesForTable = ProcessUtils.getProcessesForTable(metaData, tableName);
            processesForTable.sort((a, b) => a.label.localeCompare(b.label));
            setTableProcesses(processesForTable); // these are the ones to show in the dropdown
            setAllTableProcesses(ProcessUtils.getProcessesForTable(metaData, tableName, true)); // these include hidden ones (e.g., to find the bulks)

            ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
            // now that we know the table - build a default view - initially, only used by SavedViews component, for showing if there's anything to be saved. //
            // but also used when user selects new-view from the view menu                                                                                    //
            ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
            const newDefaultView = buildTableDefaultView(tableMetaData);
            setTableDefaultView(newDefaultView);

            setPageState("loadedMetaData");
         }
         catch (e)
         {
            setPageState("error");
            //////////////////////////
            // @ts-ignore e.message //
            //////////////////////////
            setAlertContent("Error loading table: " + e?.message ?? "Details not available.");
         }
      })();
   }

   //////////////////////////////////////////////////////////////////////////////////////////////////////
   // handle the secondary page state - after meta-data is in state - by figuring out the current view //
   //////////////////////////////////////////////////////////////////////////////////////////////////////
   if (pageState == "loadedMetaData")
   {
      console.log("page state is loadedMetaData - going to loadingView...");
      setPageState("loadingView");

      (async () =>
      {
         //////////////////////////////////////////////////////////////////////////////////////////////
         // once we've loaded meta data, let's check the location to see if we should open a process //
         //////////////////////////////////////////////////////////////////////////////////////////////
         try
         {
            /////////////////////////////////////////////////////////////////
            // the path for a process looks like: .../table/process        //
            // so if our tableName is in the -2 index, try to open process //
            //                                                             //
            // query-screen only, for the same reason as the ?filter= and  //
            // savedView paths: the location is the host page's, not ours. //
            /////////////////////////////////////////////////////////////////
            if (pathParts[pathParts.length - 2] === tableName && usage == "queryScreen")
            {
               const processName = pathParts[pathParts.length - 1];
               const processList = allTableProcesses.filter(p => p.name == processName);
               if (processList.length > 0)
               {
                  setActiveModalProcess(processList[0]);
               }
               else if (metaData?.processes.has(processName))
               {
                  ///////////////////////////////////////////////////////////////////////////////////////
                  // check for generic processes - should this be a specific attribute on the process? //
                  ///////////////////////////////////////////////////////////////////////////////////////
                  setActiveModalProcess(metaData?.processes.get(processName));
               }
               else
               {
                  console.log(`Couldn't find process named ${processName}`);
               }
            }
         }
         catch (e)
         {
            console.log(e);
         }

         ////////////////////////////////////////////////////////////////////////////////////////
         // the ?filter= in the URL belongs to the HOST page (ProcessSummaryLine, ScriptViewer //
         // and openFilterInNewWindow all produce them), so only the query screen may consume  //
         // it.  A picker mounted under such a URL would otherwise parse the host's filter,    //
         // clean it up against the PICKER's tableMetaData, and activate it - discarding the   //
         // seed filter it was opened with.  Same gate as the localStorage branch below.       //
         ////////////////////////////////////////////////////////////////////////////////////////
         if (searchParams && searchParams.has("filter") && usage == "queryScreen")
         {
            //////////////////////////////////////////////////////////////////////////////////////
            // if there's a filter in the URL - then set that as the filter in the current view //
            //////////////////////////////////////////////////////////////////////////////////////
            try
            {
               ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
               // todo - some version of "you've browsed back here, so if active view (local-storage) is the same as this, then keep old... //
               ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
               console.log(`history state: ${JSON.stringify(window.history.state)}`);

               ///////////////////////////////////////////////////////////////////////////////////////////////////
               // parse the filter json into a filer object - then clean up values in it (e.g., translate PV's) //
               ///////////////////////////////////////////////////////////////////////////////////////////////////
               const filterJSON = JSON.parse(searchParams.get("filter"));
               const queryFilter = filterJSON as QQueryFilter;

               await FilterUtils.cleanupValuesInFilerFromQueryString(qController, tableMetaData, queryFilter);

               ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
               // so, URLs with filters, they might say NOT_EQUALS - but - everything else we do in here, uses NOT_EQUALS_OR_IS_NULL... //
               ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
               for (let i = 0; i < queryFilter?.criteria?.length; i++)
               {
                  const criteria = queryFilter.criteria[i];
                  if (criteria.operator == QCriteriaOperator.NOT_EQUALS)
                  {
                     criteria.operator = QCriteriaOperator.NOT_EQUALS_OR_IS_NULL;
                  }
               }

               ///////////////////////////////////////////////////////////////////////////////////////////
               // set this new query filter in the view, and activate the full view                     //
               // stuff other than the query filter should "stick" from what user had active previously //
               ///////////////////////////////////////////////////////////////////////////////////////////
               view.queryFilter = queryFilter;
               activateView(view);

               /////////////////////////////////////////////////////////////////////////////////////////////
               // make sure that we clear out any currently saved view - we're no longer in such a state. //
               /////////////////////////////////////////////////////////////////////////////////////////////
               doClearCurrentSavedView();
            }
            catch (e)
            {
               console.error(e);
               setAlertContent("Error parsing filter from URL");
            }
         }
         else if (viewIdInLocation)
         {
            if (view.viewIdentity == `savedView:${viewIdInLocation}`)
            {
               /////////////////////////////////////////////////////////////////////////////////////////////////
               // if the view id in the location is the same as the view that was most-recently active here,  //
               // then we want to act like that old view is active - but - in case the user changed anything, //
               // we want to keep their current settings as the active view - thus - use the current 'view'   //
               // state variable (e.g., from local storage) as the view to be activated.                      //
               /////////////////////////////////////////////////////////////////////////////////////////////////
               console.log(`Initializing view to a (potentially dirty) saved view (id=${viewIdInLocation})`);
               activateView(view);

               /////////////////////////////////////////////////////////////////////////////////////////////////////////
               // now fetch that savedView, and set it in state, but don't activate it - because that would overwrite //
               // anything the user may have changed (e.g., anything in the local-storage/state view).                //
               /////////////////////////////////////////////////////////////////////////////////////////////////////////
               const savedViewRecord = await fetchSavedView(viewIdInLocation);
               setCurrentSavedView(savedViewRecord);
            }
            else
            {
               ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
               // if there's a filterId in the location, but it isn't the last one the user had active, then set that as our active view //
               ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
               console.log(`Initializing view to a clean saved view (id=${viewIdInLocation})`);
               await handleSavedViewChange(viewIdInLocation);
            }
         }
         else
         {
            ///////////////////////////////////////////////////////////////////////////////////////////////
            // if the last time we were on this table, a currentSavedView was written to local storage - //
            // then navigate back to that view's URL - unless - it looks like we're on a process!        //
            //                                                                                           //
            // queryScreen usage only.  The local-storage key and urlLooksLikeProcess() both describe    //
            // the HOST page, so in a picker this branch would silently load whatever saved view the     //
            // user last activated on that table's own query screen - criteria AND columns - leaving     //
            // them searching a pre-filtered subset with nothing saying why.  (loadedFilterFromInitial-  //
            // FilterParam only shielded pickers whose host passed a filter, which for a possible-value  //
            // source with no filter of its own is nothing.)                                             //
            ///////////////////////////////////////////////////////////////////////////////////////////////
            if (usage == "queryScreen" && localStorage.getItem(currentSavedViewLocalStorageKey) && !urlLooksLikeProcess() && !loadedFilterFromInitialFilterParam)
            {
               const currentSavedViewId = Number.parseInt(localStorage.getItem(currentSavedViewLocalStorageKey));
               console.log(`returning to previously active saved view ${currentSavedViewId}`);
               navigate(`${metaData.getTablePathByName(tableName)}/savedView/${currentSavedViewId}`);
               setViewIdInLocation(currentSavedViewId);

               /////////////////////////////////////////////////////////////////////////////////////////////////////
               // return - without activating any view, and actually, reset the pageState back to loadedMetaData, //
               // so the useEffect that monitors location will see the change, and will set viewIdInLocation      //
               // so upon a re-render we'll hit this block again.                                                 //
               /////////////////////////////////////////////////////////////////////////////////////////////////////
               setPageState("loadedMetaData");
               return;
            }

            //////////////////////////////////////////////////////////////////
            // view is ad-hoc - just activate the view that was last active //
            //////////////////////////////////////////////////////////////////
            activateView(view);
         }

         setPageState("loadedView");
      })();
   }

   //////////////////////////////////////////////////////////////////////////////////////////////
   // handle the 3rd page state - after we have the view loaded - prepare the grid for display //
   //////////////////////////////////////////////////////////////////////////////////////////////
   if (pageState == "loadedView")
   {
      console.log("page state is loadedView - going to preparingGrid...");
      setPageState("preparingGrid");

      //////////////////////////////////////////////////////////////////////////////////////////////////////
      // check if any new columns have been added to the table since last time this view was activated... //
      // or if anything in the view is no longer in the table                                             //
      //////////////////////////////////////////////////////////////////////////////////////////////////////
      reconcileCurrentTableMetaDataWithView(view, "initialPageLoad");

      ////////////////////////////////////////////////////////////////////////////////////////
      // this ref may not be defined on the initial render, so, make this call in a timeout //
      ////////////////////////////////////////////////////////////////////////////////////////
      setTimeout(() =>
      {
         ////////////////
         // @ts-ignore //
         ////////////////
         basicAndAdvancedQueryControlsRef?.current?.ensureAllFilterCriteriaAreActiveQuickFilters(view.queryFilter, "defaultFilterLoaded");
      });

      console.log("finished preparing grid, going to page state ready");
      setPageState("ready");

      //////////////////////////////////////////////////////////////////
      // if we need a variant, show that prompt — unless the host has //
      // forced one (in which case it should be in state already).    //
      //////////////////////////////////////////////////////////////////
      if (tableMetaData?.usesVariants && !tableVariant && !forcedTableVariant)
      {
         promptForTableVariantSelection();
      }

      return (getLoadingScreen(isModal));
   }

   ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
   // any time these are out of sync, it means we've navigated to a different table, so we need to reload :allthethings: //
   ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
   if (tableMetaData && tableMetaData.name !== tableName)
   {
      console.log(`Found mis-match between tableMetaData.name and tableName [${tableMetaData.name}]!=[${tableName}] - reload everything.`);
      setPageState("initial");
      setTableMetaData(null);
      setColumnSortModel([]);
      setColumnsModel([]);
      setQueryFilter(new QQueryFilter());
      setQueryColumns(new PreLoadQueryColumns());
      setRows([]);
      setIsFirstRenderAfterChangingTables(true);

      return (getLoadingScreen(isModal));
   }

   /////////////////////////////////////////////////////////////////////////////////////////////
   // if the table doesn't allow QUERY, but does allow GET, don't render a data grid -        //
   // instead, try to just render a Goto Record button, in auto-open, and may-not-close modes //
   /////////////////////////////////////////////////////////////////////////////////////////////
   if (tableMetaData && !tableMetaData.capabilities.has(Capability.TABLE_QUERY) && tableMetaData.capabilities.has(Capability.TABLE_GET))
   {
      if (tableMetaData?.usesVariants && (!tableVariant || tableVariantPromptOpen))
      {
         return (
            <TableVariantDialog table={tableMetaData} isOpen={true} closeHandler={(value: QTableVariant) =>
            {
               setTableVariantPromptOpen(false);
               setTableVariant(value);
            }} />
         );
      }

      ////////////////////////////////////////////////////////////////////////////////////
      // if the table uses variants, then put the variant-selector into the goto dialog //
      ////////////////////////////////////////////////////////////////////////////////////
      let gotoVariantSubHeader = <></>;
      if (tableMetaData?.usesVariants)
      {
         gotoVariantSubHeader = <Box mb={2}>{getTableVariantHeader(tableVariant)}</Box>;
      }

      return (
         <GotoRecordButton metaData={metaData} tableMetaData={tableMetaData} tableVariant={tableVariant} autoOpen={true} buttonVisible={false} mayClose={false} subHeader={gotoVariantSubHeader} />
      );
   }

   //////////////////////////////////////////////
   // render an error screen (alert) if needed //
   //////////////////////////////////////////////
   if (pageState == "error")
   {
      console.log(`page state is ${pageState}... rendering an alert...`);
      const errorBody = <Box py={3}><Alert severity="error">{alertContent}</Alert></Box>;
      return errorBody;
   }

   ///////////////////////////////////////////////////////////
   // render a loading screen if the page state isn't ready //
   ///////////////////////////////////////////////////////////
   if (pageState != "ready")
   {
      console.log(`page state is ${pageState}... no-op while those complete async's run...`);
      return (getLoadingScreen(isModal));
   }

   ///////////////////////////////////////////////////////////////////////////////////////////
   // if the table isn't loaded yet, display loading screen.                                //
   // this shouldn't be possible, to be out-of-sync with pageState, but just as a fail-safe //
   ///////////////////////////////////////////////////////////////////////////////////////////
   if (!tableMetaData)
   {
      return (getLoadingScreen(isModal));
   }

   let savedViewsComponent = null;
   if (metaData && metaData.processes.has("querySavedView"))
   {
      savedViewsComponent = (<SavedViews useSavedViewsResult={useSavedViewsResult} metaData={metaData} tableMetaData={tableMetaData} view={view} viewAsJson={viewAsJson} currentSavedView={currentSavedView} tableDefaultView={tableDefaultView} viewOnChangeCallback={handleSavedViewChange} loadingSavedView={loadingSavedView} queryScreenUsage={usage} />);
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   const buildColumnMenu = () =>
   {
      //////////////////////////////////////////
      // default (no saved view, and "clean") //
      //////////////////////////////////////////
      let buttonBackground = "unset";
      let buttonBorder = colors.grayLines.main + " !important";
      let buttonColor = colors.gray.main;
      let buttonVariant: "outlined" | "contained" | "text" = "outlined";
      let buttonColorName: "secondary" | "primary" = "secondary";
      let buttonState: string = "empty";

      ////////////////////////////////////////////////////////////////////////////////////////////////////////////
      // diff the current view with either the current saved one, if there's one active, else the table default //
      ////////////////////////////////////////////////////////////////////////////////////////////////////////////
      const baseView = currentSavedView ? JSON.parse(currentSavedView.values.get("viewJson")) as RecordQueryView : tableDefaultView;
      const viewDiffs: string[] = [];
      SavedViewUtils.diffColumns(tableMetaData, baseView, view, viewDiffs);

      if (viewDiffs.length == 0 && currentSavedView)
      {
         /////////////////////////////////////////////////////////////////
         // if 's a saved view, and it's "clean", show it in main style //
         /////////////////////////////////////////////////////////////////
         buttonBackground = accentColor;
         buttonBorder = accentColor;
         buttonColor = "#FFFFFF";
         buttonColorName = "primary";
         buttonVariant = "contained";
         buttonState = "clean";
      }
      else if (viewDiffs.length > 0)
      {
         ///////////////////////////////////////////////////
         // else if there are diffs, show alt/light style //
         ///////////////////////////////////////////////////
         buttonBackground = accentColorLight + " !important";
         buttonBorder = accentColorLight + " !important";
         buttonColor = accentColor + " !important";
         buttonColorName = "primary";
         buttonVariant = "contained";
         buttonState = "dirty";
      }

      const columnMenuButtonStyles = {
         borderRadius: "0.75rem",
         border: `1px solid ${buttonBorder}`,
         color: buttonColor,
         textTransform: "none",
         fontWeight: 500,
         fontSize: "0.875rem",
         boxShadow: "none",
         p: "0.5rem",
         backgroundColor: buttonBackground,
         "&:focus:not(:hover)": {
            color: buttonColor,
            backgroundColor: buttonBackground,
         },
         "&:hover": {
            color: buttonColor,
            backgroundColor: buttonBackground,
            boxShadow: "none",
            filter: "unset"
         }
      };

      return (<Box order="2">
         <FieldListMenu
            idPrefix="columns"
            tableMetaData={tableMetaData}
            showTableHeaderEvenIfNoExposedJoins={true}
            omitExposedJoins={omitExposedJoins}
            placeholder="Search Fields"
            buttonProps={{variant: buttonVariant, sx: columnMenuButtonStyles, ["data-button-state"]: buttonState, color: buttonColorName}}
            buttonChildren={<><Icon sx={{mr: "0.5rem"}}>view_week_outline</Icon> Columns ({view.queryColumns.getVisibleColumnCount()}) <Icon sx={{ml: "0.5rem"}}>keyboard_arrow_down</Icon></>}
            isModeToggle={true}
            toggleStates={view.queryColumns.getVisibilityToggleStates()}
            handleToggleField={handleChangeOneColumnVisibility}
            requiredCapability={Capability.FIELD_SELECTABLE}
            fieldEndAdornment={<Tooltip title="Jump to Field"><Icon sx={{color: "var(--qqq-grey-600, #757575)"}}>my_location</Icon></Tooltip>}
            handleAdornmentClick={(field, table) => locateColumnInGrid(field, table)}
         />
      </Box>);
   };


   /***************************************************************************
    **
    ***************************************************************************/
   function doRecordAnalytics(model: AnalyticsModel)
   {
      try
      {
         recordAnalytics(model);
      }
      catch (e)
      {
         console.log(`Error recording analytics: ${e}`);
      }
   }

   //////////////////////////////////////////////////////////////////////////////////////////////////////////
   // these numbers help set the height of the grid (so page won't scroll) based on space above & below it //
   //////////////////////////////////////////////////////////////////////////////////////////////////////////
   let spaceBelowGrid = 40;
   let spaceAboveGrid = 205;
   if (tableMetaData?.usesVariants)
   {
      spaceAboveGrid += 30;
   }

   if (mode == "advanced")
   {
      spaceAboveGrid += 60;
   }

   if (isModal)
   {
      spaceAboveGrid += 130;
   }

   const itemsShownInActionsMenu = new ItemsShownInMenu();

   ////////////////////////
   // main screen render //
   ////////////////////////
   const body = (
      <React.Fragment>
         <Box display="flex" justifyContent="space-between">
            <Box>
               <Typography textTransform="capitalize" variant="h3">
                  {pageLoadingState.isLoading() && ""}
                  {pageLoadingState.isLoadingSlow() && "Loading..."}
                  {pageLoadingState.isNotLoading() && !isModal && getPageHeader(tableMetaData, visibleJoinTables, tableVariant)}
               </Typography>
               {
                  ////////////////////////////////////////////////////////////////
                  // In modal mode the standard page header (with the variant   //
                  // indicator) is suppressed.  When the host forced a variant, //
                  // render a small read-only indicator inside the modal so the //
                  // user still sees what scopes the query.                     //
                  ////////////////////////////////////////////////////////////////
                  pageLoadingState.isNotLoading() && isModal && forcedTableVariant && tableMetaData?.usesVariants && (
                     <Box mt={0.5}>{getTableVariantHeader(tableVariant)}</Box>
                  )
               }
            </Box>
            {
               !isModal &&
               <Box whiteSpace="nowrap">
                  <GotoRecordButton metaData={metaData} tableMetaData={tableMetaData} />
                  {metaData && tableMetaData && additionalActionMenus.map((menu, index) => (
                     <RecordViewMenu key={`query-additional-${index}`} tableMetaData={tableMetaData} menu={menu} actions={queryMenuActions} accordionStorageScope="query" />
                  ))}
                  <Box display="inline-block" width="150px">
                     {
                        tableMetaData &&
                        <>
                           <QActionsMenuButton isOpen={actionsMenuAnchorElement} onClickHandler={(event: any) => setActionsMenuAnchorElement(event.currentTarget)} label={actionMenu?.label} qIcon={actionMenu?.icon} />
                           <Menu
                              anchorEl={actionsMenuAnchorElement}
                              anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                              transformOrigin={{vertical: "top", horizontal: "right"}}
                              open={Boolean(actionsMenuAnchorElement)}
                              onClose={() => setActionsMenuAnchorElement(null)}
                              keepMounted
                           >
                              {actionMenu?.items.map((item, index) => (
                                 <ActionMenuItem key={index} tableMetaData={tableMetaData} menuItem={item} actions={queryMenuActions} closeMenu={() => setActionsMenuAnchorElement(null)} itemsShown={itemsShownInActionsMenu} accordionStorageScope="query" />
                              ))}
                           </Menu>
                        </>
                     }
                  </Box>
                  {
                     table.capabilities.has(Capability.TABLE_INSERT) && table.insertPermission &&
                     <QCreateNewButton tablePath={metaData?.getTablePathByName(tableName)} />
                  }
               </Box>
            }
         </Box>
         <div className="recordQuery">
            {/*
            ////////////////////////////////////////////////////
            // see code in ExportMenuItem that would use this //
            ////////////////////////////////////////////////////
            <iframe id="exportIFrame" name="exportIFrame">
               <form method="post" target="_self">
                  <input type="hidden" id="authorizationInput" name="Authorization" />
               </form>
            </iframe>
            */}
            <Box mb={3}>
               {
                  alertContent ? (
                     <Collapse in={Boolean(alertContent)}>
                        <Alert severity="error" sx={{mt: 1.5, mb: 0.5}} onClose={() => setAlertContent(null)}>{alertContent}</Alert>
                     </Collapse>
                  ) : null
               }
               {
                  (tableLabel && showSuccessfullyDeletedAlert) ? (
                     <Collapse in={Boolean(showSuccessfullyDeletedAlert)}>
                        <Alert color="success" sx={{mt: 1.5, mb: 0.5}} onClose={() => setShowSuccessfullyDeletedAlert(false)}>{`${tableLabel} successfully deleted`}</Alert>
                     </Collapse>
                  ) : null
               }
               {
                  (successAlert) ? (
                     <Collapse in={Boolean(successAlert)}>
                        <Alert color="success" sx={{mt: 1.5, mb: 0.5}} onClose={() => setSuccessAlert(null)}>{successAlert}</Alert>
                     </Collapse>
                  ) : null
               }
               {
                  (infoAlert) ? (
                     <Collapse in={Boolean(infoAlert)}>
                        <Alert color="info" sx={{mt: 1.5, mb: 0.5}} onClose={() => setInfoAlert(null)} icon={<Icon>info_outline</Icon>}>{infoAlert}</Alert>
                     </Collapse>
                  ) : null
               }
               {
                  (warningAlert) ? (
                     <Collapse in={Boolean(warningAlert)}>
                        <Alert color="warning" icon={<Icon>warning</Icon>} sx={{mt: 1.5, mb: 0.5}} onClose={() => setWarningAlert(null)}>{warningAlert}</Alert>
                     </Collapse>
                  ) : null
               }
               {
                  warningAlertList.map((message, index) => (
                     <Collapse key={`${index}-${message}`} in={true}>
                        <Alert color="warning" icon={<Icon>warning</Icon>} sx={{mt: 1.5, mb: 0.5}} onClose={() => deleteWarningAlert(index)}>{message}</Alert>
                     </Collapse>
                  ))
               }
               {
                  (errorAlert) ? (
                     <Collapse in={Boolean(errorAlert)}>
                        <Alert color="error" icon={<Icon>error_outline</Icon>} sx={{mt: 1.5, mb: 0.5}} onClose={() => setErrorAlert(null)}>{errorAlert}</Alert>
                     </Collapse>
                  ) : null
               }

               {
                  !isPreview && metaData && tableMetaData &&
                  <BasicAndAdvancedQueryControls
                     ref={basicAndAdvancedQueryControlsRef}
                     lockedCriteriaCount={lockedCriteriaCount}
                     metaData={metaData}
                     tableMetaData={tableMetaData}
                     apiVersion={apiVersion}
                     tableVariant={tableVariant}
                     queryFilter={queryFilter}
                     queryFilterJSON={JSON.stringify(queryFilter)}
                     setQueryFilter={doSetQueryFilter}
                     quickFilterFieldNames={quickFilterFieldNames}
                     setQuickFilterFieldNames={doSetQuickFilterFieldNames}
                     gridApiRef={gridApiRef}
                     mode={mode}
                     queryScreenUsage={usage}
                     allowVariables={allowVariables}
                     setMode={doSetMode}
                     savedViewsComponent={savedViewsComponent}
                     columnMenuComponent={buildColumnMenu()}
                     omitExposedJoins={omitExposedJoins}
                     useSavedViewsResult={useSavedViewsResult}
                     viewOnChangeCallback={handleSavedViewChange}
                     currentSavedView={currentSavedView}
                     activeView={view}
                  />
               }

               <Card>
                  <Box height="100%">
                     <RowEditContext.Provider value={rowEditContextValue}>
                        <ExpandedGridRowsContext.Provider value={expandedGridRowsContextValue}>
                           <DataGridPro
                              apiRef={gridApiRef}
                              slots={{
                                 row: EditableRowComponent,
                                 toolbar: CustomToolbar,
                                 pagination: CustomPagination,
                                 loadingOverlay: CustomLoadingOverlay,
                                 columnMenu: CustomColumnMenu,
                                 filterPanel: CustomFilterPanel,
                                 columnSortedDescendingIcon: null, columnSortedAscendingIcon: null, columnUnsortedIcon: null,
                                 columnHeaderFilterIconButton: CustomColumnHeaderFilterIconButton,
                              }}
                              slotProps={{
                                 filterPanel:
                                 {
                                    tableMetaData: tableMetaData,
                                    queryScreenUsage: usage,
                                    lockedCriteriaCount: lockedCriteriaCount,
                                    lockedCriteriaIsOverridingBooleanOperator: lockedCriteriaIsOverridingBooleanOperator.current,
                                    metaData: metaData,
                                    queryFilter: queryFilter,
                                    updateFilter: doSetQueryFilter,
                                    allowVariables: allowVariables,
                                    omitExposedJoins: omitExposedJoins,
                                 }
                              }}
                              localeText={{
                                 columnMenuSortAsc: "Sort ascending",
                                 columnMenuSortDesc: "Sort descending",
                              }}
                              pinnedColumns={pinnedColumns}
                              onPinnedColumnsChange={handlePinnedColumnsChange}
                              pagination
                              paginationMode="server"
                              sortingMode="server"
                              filterMode="server"
                              paginationModel={{page: pageNumber, pageSize: rowsPerPage}}
                              pageSizeOptions={[10, 25, 50, 100, 250]}
                              onPaginationModelChange={(model) =>
                              {
                                 if (model.pageSize != rowsPerPage)
                                 {
                                    handleRowsPerPageChange(model.pageSize);
                                 }
                                 else if (model.page != pageNumber)
                                 {
                                    handlePageNumberChange(model.page);
                                 }
                              }}
                              checkboxSelection={usage == "queryScreen" || usage == "recordPicker"}
                              disableMultipleRowSelection={isSingleSelectPicker}
                              disableRowSelectionOnClick
                              autoHeight={false}
                              rows={rows}
                              ////////////////////////////////////////////////////////////////////////////////
                              // Auto-grow the editing row ONLY when it has extra content that needs        //
                              // vertical space — field-level validation errors (rendered beneath inputs)   //
                              // or a row-level banner.  While editing with no errors / no banner, keep the //
                              // row at the density-driven default height so entering edit doesn't visibly  //
                              // push the row taller.                                                       //
                              ////////////////////////////////////////////////////////////////////////////////
                              getRowHeight={(params) => (expandedRowIds.has(params.id) || (params.id === editingRowId && rowEditor.rowBannerState.kind !== "none") ? "auto" : null)}
                              ///////////////////////////////////////////////////////////////////////////////
                              // MUI X v7 only re-renders rows with the "auto" height when the estimated   //
                              // total page height CHANGES - if the estimate for a newly-auto row equals   //
                              // the cached fixed height (the default), hydrateRowsMeta's dimension update //
                              // no-ops (deep-equal bail), the row keeps its inline max-height, and the    //
                              // ResizeObserver never attaches - so the editing row would never grow when  //
                              // a field error appears.  Returning a larger estimate for the expanded row  //
                              // forces the dimension change / re-render; the observer then corrects the   //
                              // height to the real measured value.                                        //
                              ///////////////////////////////////////////////////////////////////////////////
                              getEstimatedRowHeight={(params) => (expandedRowIds.has(params.id) || (params.id === editingRowId && rowEditor.rowBannerState.kind !== "none") ? 80 : null)}
                              columns={columnsModel}
                              rowCount={totalRecords === null || totalRecords === undefined ? 0 : totalRecords}
                              onRowClick={usage == "queryScreen" ? handleRowClick : (usage == "recordPicker" ? handlePickerRowClick : null)}
                              density={density}
                              onDensityChange={handleDensityChange}
                              loading={loading}
                              columnVisibilityModel={columnVisibilityModel}
                              onColumnVisibilityModelChange={handleColumnVisibilityChange}
                              onColumnOrderChange={handleColumnOrderChange}
                              onColumnResize={handleColumnResize}
                              onRowSelectionModelChange={handleSelectionChanged}
                              onSortModelChange={handleSortChange}
                              sortingOrder={["asc", "desc"]}
                              onColumnHeaderClick={handleColumnHeaderClick}
                              getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd") + (expandedRowIds.has(params.id) ? " qqq-multi-line-expanded-row" : "")}
                              getCellClassName={(params) => (params.field === pulsingField ? "qqq-locate-pulse" : "")}
                              getRowId={(row) => row.__rowIndex}
                              rowSelectionModel={rowSelectionModel}
                              hideFooterSelectedRowCount={true}
                              disableVirtualization={editingRowId != null}
                              ////////////////////////////////////////////////////////////////////////////////////
                              // mui-x v7 virtualizes rows+columns by pixels (default ~150px) where v5 buffered //
                              // whole rows/columns (rowBuffer={10}) - keep off-viewport cells in the DOM so    //
                              // selenium suites and downstream consumers can assert cells behind the scroll    //
                              ////////////////////////////////////////////////////////////////////////////////////
                              columnBufferPx={10000}
                              rowBufferPx={10000}
                              sx={{
                                 border: 0,
                                 height: `calc(100vh - ${spaceAboveGrid + spaceBelowGrid}px - var(--qqq-branded-header-height, 0px))`,
                                 ...(isSingleSelectPicker ? singleSelectPickerRadioSx(accentColor) : {}),
                                 "@keyframes qqq-locate-pulse-kf": {
                                    "0%": {backgroundColor: accentColorLight},
                                    "100%": {backgroundColor: "transparent"},
                                 },
                                 "& .qqq-locate-pulse": {
                                    animation: "qqq-locate-pulse-kf 1s ease-out",
                                 },
                                 ...(pulsingField
                                    ? {
                                       [`& .MuiDataGrid-columnHeader[data-field="${pulsingField}"]`]: {
                                          animation: "qqq-locate-pulse-kf 1s ease-out",
                                       },
                                    }
                                    : {}),
                              }}
                           />
                           <HoverRowActionsOverlay
                              apiRef={gridApiRef}
                              isRowEditable={!!rowEditCanEdit}
                              onEnterEdit={rowEditOnEnterEdit}
                           />
                           <SavedRowOverlay apiRef={gridApiRef} onEnterEdit={rowEditOnEnterEdit} />
                           <EditingRowBannerOverlay apiRef={gridApiRef} />
                        </ExpandedGridRowsContext.Provider>
                     </RowEditContext.Provider>
                  </Box>
               </Card>
            </Box>

            {
               activeModalProcess && tableMetaData &&
               <Modal open={activeModalProcess !== null} onClose={(event, reason) => closeModalProcess(event, reason)}>
                  <div className="modalProcess">
                     <ProcessRun process={activeModalProcess} isModal={true} table={tableMetaData} recordIds={recordIdsForProcess} closeModalHandler={closeModalProcess} />
                  </div>
               </Modal>
            }

            {
               tableMetaData && tableMetaData.usesVariants &&
               <TableVariantDialog table={tableMetaData} isOpen={tableVariantPromptOpen} closeHandler={(value: QTableVariant) =>
               {
                  setTableVariantPromptOpen(false);
                  setTableVariant(value);
               }} />
            }

            {
               columnStatsFieldName &&
               <Modal open={columnStatsFieldName !== null} onClose={(event, reason) => closeColumnStats(event, reason)}>
                  <div className="columnStatsModal">
                     <Box sx={{position: "absolute", overflowY: "auto", maxHeight: "100%", width: "100%"}}>
                        <Card sx={{my: 5, mx: "auto", pb: 0, maxWidth: "1024px"}}>
                           <Box component="div">
                              <ColumnStats tableMetaData={tableMetaData} fieldMetaData={columnStatsField} fieldTableName={columnStatsFieldTableName} filter={filterForColumnStats} />
                              <Box p={3} display="flex" flexDirection="row" justifyContent="flex-end">
                                 <QCancelButton label="Close" onClickHandler={() => closeColumnStats(null, null)} disabled={false} />
                              </Box>
                           </Box>
                        </Card>
                     </Box>
                  </div>
               </Modal>
            }

            {/* todo - would work with advanced copy-values mode referenced above (should go into its own component probably
               copyingFieldName &&
               <Modal open={copyingFieldName !== null} onClose={(event, reason) => closeCopyingDialog(event, reason)}>
                  <div className="columnStatsModal">
                     <Box sx={{position: "absolute", overflowY: "auto", maxHeight: "100%", width: "100%"}}>
                        <Card sx={{my: 5, mx: "auto", pb: 0, maxWidth: "600px"}}>
                           <Box component="div">
                              <Box p={3}>
                                 <Typography variant="h5">Copy Values</Typography>
                              </Box>
                              <Box p={3} fontSize={14} sx={{
                                 "& .MuiFormGroup-root": {
                                    fontWeight: 700,
                                    paddingBottom: "1rem",
                                 },
                                 "& .MuiFormControlLabel-root": {
                                    minWidth: "200px",
                                    whiteSpace: "nowrap",
                                    "& .MuiFormControlLabel-label": {
                                       fontWeight: 400
                                    }
                                 }
                              }}>
                                 <RadioGroup name="x">
                                    Number of values:
                                    <Box display="flex">
                                       <FormControlLabel value="y" control={<Radio size="small" onChange={(event, checked) => console.log(checked)} checked={true} />} label={"Current page"} />
                                       <FormControlLabel value="z" control={<Radio size="small" onChange={(event, checked) => console.log(checked)} />} label={"Full query result"} />
                                    </Box>
                                 </RadioGroup>
                                 <RadioGroup name="y">
                                    Blank values:
                                    <Box display="flex">
                                       <FormControlLabel value="y" control={<Radio size="small" onChange={(event, checked) => console.log(checked)} checked={true} />} label={"Include blanks"} />
                                       <FormControlLabel value="z" control={<Radio size="small" onChange={(event, checked) => console.log(checked)} />} label={"Omit blanks"} />
                                    </Box>
                                 </RadioGroup>
                                 <RadioGroup name="z">
                                    Duplicated values:
                                    <Box display="flex">
                                       <FormControlLabel value="y" control={<Radio size="small" onChange={(event, checked) => console.log(checked)} checked={true} />} label={"Include all duplicates"} />
                                       <FormControlLabel value="z" control={<Radio size="small" onChange={(event, checked) => console.log(checked)} />} label={"Only include distinct values"} />
                                    </Box>
                                 </RadioGroup>
                              </Box>
                              <Box p={3} display="flex" flexDirection="row" justifyContent="flex-end">
                                 <QCancelButton label="Cancel" onClickHandler={() => closeCopyingDialog(null, null)} disabled={false} />
                                 <QSaveButton label="OK" onClickHandler={() => doCopy()} disabled={false} iconName="check" />
                              </Box>
                           </Box>
                        </Card>
                     </Box>
                  </div>
               </Modal>
            */}
         </div>
      </React.Fragment>
   );

   return body;
});


RecordQuery.defaultProps = {
   table: null,
   apiVersion: null,
   usage: "queryScreen",
   launchProcess: null,
   isModal: false,
   initialQueryFilter: null,
   initialColumns: null,
};


export default RecordQuery;
